import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  Autocomplete,
  Button,
  LegacyCard,
  ContextualSaveBar,
  FormLayout,
  Frame,
  Icon,
  Layout,
  Page,
  SkeletonBodyText,
  SkeletonDisplayText,
  SkeletonPage,
  TextContainer,
  TextField,
  Text,
  Toast,
  Banner,
  Tooltip,
  Checkbox,
  Scrollable,
  EmptyState,
  LegacyStack,
  UnstyledLink,
  Select,
  Thumbnail,
  DropZone,
  HorizontalStack,
  Tag,
  Modal,
  ResourceList,
  ResourceItem,
} from '@shopify/polaris';

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import axios, { AxiosError } from 'axios';

import './VariantDetails.scss';
import { TopBarMarkup, NavigationMarkup, contextControlMarkup } from '../../../../../components';

import {
  AddNoteMajor,
  CancelSmallMinor,
  DeleteMinor,
  DeleteMajor,
  FraudProtectMajor,
  NoteMinor,
  PlusMinor,
  QuestionMarkMajor,
  SearchMinor,
} from '@shopify/polaris-icons';
import { SortableItemWarranty, SortableItemQuotation, SortableItemPricingRule } from '../../Components';

import {
  PricingRule,
  QuotationField,
  PricingTable,
  Variant,
  Company,
  Warranty,
  Renewal,
  Automation,
  VariantFileType,
  TBlockingCondition,
  TCondition,
  RuleTypes,
  blockingConditionsRuleOptions,
  Discount,
  EnablingField,
} from '../../../../../types';
import { useUser } from '../../../../../utils/PrivateRoute';
import { useParams } from 'react-router-dom';
import EditableTable from '../../Components/EditableTable/EditableTable';
import EditableRows from '../../Components/EditableTable/EditableRows';
import { TColumnContent } from '../../../../../types/editableTable';
import saveAs from 'file-saver';
import { parseVariantFileType } from '../../../../../utils/Common';
import { has, set } from 'lodash';
import { Value } from 'sass';

type AutocompleteCompanyOption = {
  label: string;
  value: string;
  company: Company;
};

export function VariantDetails() {
  const { user } = useUser();
  const params = useParams();

  const skipToContentRef = useRef<HTMLAnchorElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonSpinning, setButtonSpinning] = useState(false);
  const [buttonSpinningUpload, setButtonSpinningUpload] = useState(false);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [update, setUpdate] = useState(false);
  const [missingUserError, setMissingUserError] = useState(false);
  /** File */
  const [error, setError] = useState(false);
  const [txtFileUploadValue, setTxtFileUploadValue] = useState('');
  const [tmpFileUploadValue, setTmpFileUploadValue] = useState<any>();
  const [selectedFileUpload, setSelectedFileUpload] = useState<string[]>([]);
  const [optionsFileUpload, setOptionsFileUpload] = useState<{ label: string; value: string }[]>([]);
  const [activeFileUpload, setActiveFileUpload] = useState(false);
  const [isAdderFileUpload, setIsAdderFileUpload] = useState(false);
  const [selectedType, setSelectedType] = useState<VariantFileType>(VariantFileType.INFORMATION_SET);

  const [fileID, setFileID] = useState<string[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);

  const [fileUploadActive, setFileUploadActive] = useState(false);
  const toggleFileUploadActive = useCallback(() => setFileUploadActive((fileUploadActive) => !fileUploadActive), []);

  const [fileDeletedActive, setFileDeletedActive] = useState(false);
  const toggleFileDeletedActive = useCallback(() => setFileDeletedActive((fileDeletedActive) => !fileDeletedActive), []);

  const typeOptions = [
    { label: 'Set Informativo', value: VariantFileType.INFORMATION_SET },
    { label: 'Informativa Precontrattuale', value: VariantFileType.PRECONTRACTUAL_INFORMATION },
  ];

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((active) => !active), []);

  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const toggleMobileNavigationActive = useCallback(() => setMobileNavigationActive((mobileNavigationActive) => !mobileNavigationActive), []);

  const handleMobileNavigation = (data: boolean) => {
    setMobileNavigationActive(!data);
  };

  /**
   * Re-order methods
   */
  const reorderState = (state: any, setState: (new_state: any) => void, new_order: string[]) => {
    // Each item in state has a position attribute, we need to update it
    // with the new order
    const new_state = state.map((item: any, index: number) => {
      item.position = new_order[index];
      return item;
    });

    setState(new_state);
  };

  /**
   * Variant states
   */
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Variant type
  const [selectedVariantType, setSelectedVariantType] = useState('basic');
  const variantTypeOptions = [
    { label: 'Base', value: 'basic' },
    { label: 'API', value: 'api' },
  ];

  // Api identifier
  const [apiIdentifier, setApiIdentifier] = useState('');

  // Group policy
  const [groupPolicy, setGroupPolicy] = useState(false);

  // To be quoted
  const [toBeQuoted, setToBeQuoted] = useState(false);

  // Direct emission
  const [directEmission, setDirectEmission] = useState(false);

  // Bank Transfer
  const [bankTransfer, setBankTransfer] = useState(false);

  // Disabling notifications
  const [disableNotifications, setDisableNotifications] = useState(false);

  // Blocking conditions
  const [hasBlockingConditions, setHasBlockingConditions] = useState(false);
  const [blockingConditions, setBlockingConditions] = useState<TBlockingCondition[]>([]);

  // Individual sale
  const [individualSale, setIndividualSale] = useState(false);
  const [quoteEmailTemplate, setQuoteEmailTemplate] = useState('');
  const [policyEmailTemplate, setPolicyEmailTemplate] = useState('');
  const [renewalEmailTemplate, setRenewalEmailTemplate] = useState('');

  // Company
  const [deselectedCompaniesOptions, setDeselectedCompaniesOptions] = useState<AutocompleteCompanyOption[]>([]);
  const [selectedCompanyOptions, setSelectedCompanyOptions] = useState<string[]>([]);
  const [companyInputValue, setCompanyInputValue] = useState('');
  const [companyOptions, setCompanyOptions] = useState(deselectedCompaniesOptions);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);

  // Quotation
  const [quotation, setQuotation] = useState(false);
  const [groupPolicyBroker, setGroupPolicyBroker] = useState('');
  const [groupPolicyBranch, setGroupPolicyBranch] = useState('');
  const [groupPolicyNumber, setGroupPolicyNumber] = useState('');
  const [groupPolicyInclusionNumber, setGroupPolicyInclusionNumber] = useState('');

  const [price, setPrice] = useState('');
  const [salePrice, setSalePrice] = useState('');

  // Quotation fields
  const quotationFieldsType = [
    { label: 'Data', value: 'date' },
    { label: 'Array', value: 'array' },
    { label: 'Numero', value: 'number' },
    { label: 'Percentuale', value: 'percentage' },
    { label: 'Valuta', value: 'currency' },
    { label: 'Testo', value: 'text' },
    { label: 'Checkbox', value: 'boolean' },
    { label: 'Select', value: 'select' },
  ];

  const [quotationFields, setQuotationFields] = useState<QuotationField[]>([]);

  // Pricing table
  const [pricingTable, setPricingTable] = useState<PricingTable>({
    row: '',
    row_labels: [],
    column: '',
    column_labels: [],
    values: [],
    is_range: false,
  });
  const [hasPricingTable, setHasPricingTable] = useState(true);
  const [pricingTableExists, setPricingTableExists] = useState(false);
  const [rows, setRows] = useState<string>('');
  const [columns, setColumns] = useState<string>('');

  const handleRowsChange = useCallback((e: string) => {
    setRows(e);
  }, []);

  const handleColumnsChange = useCallback((e: string) => {
    setColumns(e);
  }, []);

  const [columnLabels, setColumnLabels] = useState<Array<TColumnContent>>([{ columnType: 'text', columnTitle: '' }]);
  const [rowLabels, setRowsLabels] = useState<Array<Array<any>>>([]);

  // Discount
  const [hasDiscount, setHasDiscount] = useState(false);
  const [discount, setDiscount] = useState<Discount | undefined>();

  const discountValueTypeOptions = [
    { label: 'Importo fisso', value: 'fixed_amount' },
    { label: 'Percentuale', value: 'percentage' },
    { label: 'Tabella dei prezzi', value: 'pricing_table' },
    { label: 'Nessuno', value: 'none' },
  ];

  // Discount Pricing table
  const [discountPricingTable, setDiscountPricingTable] = useState<PricingTable>({
    row: '',
    row_labels: [],
    column: '',
    column_labels: [],
    values: [],
    is_range: false,
  });
  const [hasDiscountPricingTable, setHasDiscountPricingTable] = useState(false);
  const [rowsDiscount, setRowsDiscount] = useState<string>('');
  const [columnsDiscount, setColumnsDiscount] = useState<string>('');

  const handleDiscountRowsChange = useCallback((e: string) => {
    setRowsDiscount(e);
  }, []);

  const handleDiscountColumnsChange = useCallback((e: string) => {
    setColumnsDiscount(e);
  }, []);

  const [columnDiscountLabels, setColumnDiscountLabels] = useState<Array<TColumnContent>>([{ columnType: 'text', columnTitle: '' }]);
  const [rowDiscountLabels, setRowsDiscountLabels] = useState<Array<Array<any>>>([]);

  // Enabler fields for discount
  const [hasEnablerFields, setHasEnablerFields] = useState(false);
  const [enablerField, setEnablerField] = useState<EnablingField>({
    name: '',
    value: '',
    rule: RuleTypes.EQUAL,
  });

  // Commission
  const [hasCommission, setHasCommission] = useState(false);
  const [commission, setCommission] = useState({
    value: '0,00',
    value_type: 'fixed_amount',
  });

  const commissionValueTypeOptions = useMemo(
    () => [
      { label: 'Importo', value: 'fixed_amount' },
      { label: 'Percentuale', value: 'percentage' },
    ],
    [],
  );

  // Management price
  const [hasManagementPrice, setHasManagementPrice] = useState(false);
  const [managementPrice, setManagementPrice] = useState({
    value: '0,00',
    value_type: 'fixed_amount',
  });

  // Shared Notification
  const [hasSharedNotification, setHasSharedNotification] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [tagError, setTagError] = useState(false);

  // Automation Bot
  const [hasAutomationBot, setHasAutomationBot] = useState(false);
  const [automationName, setAutomationName] = useState('better-bookings');
  const [automationBotError, setAutomationBotError] = useState('');
  const [automationOptions, setAutomationOptions] = useState([]);

  // Renewal
  const [renewal, setRenewal] = useState<Renewal>({
    automatic: false,
    to_be_confirmed: false,
  });

  // Pricing rules
  const [hasPricingRules, setHasPricingRules] = useState(false);
  const pricingRulesNames = [{ label: 'Prezzo minimo', value: 'minimum_price' }];
  const pricingRulesTypes = [
    { label: 'Importo', value: 'fixed_amount' },
    { label: 'Percentuale', value: 'percentage' },
  ];

  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);

  // Variants
  const [hasWarranties, setHasWarranties] = useState(false);
  const [variants, setVariants] = useState<Variant[]>([]);

  const variantWarrantiesModifierType = [
    { label: 'Import Fisso', value: 'fixed_price' },
    { label: 'Percentuale', value: 'percentage' },
    { label: 'Nessuno', value: 'none' },
  ];

  const [variantWarranties, setVariantWarranties] = useState<Warranty[]>([]);

  // Files
  const [files, setFiles] = useState<File[]>([]);
  const [openFileDialog, setOpenFileDialog] = useState(false);

  // Variant default state
  const [defaultState, setDefaultState] = useState({
    name: '',
    description: '',
    price: '',
    salePrice: '',
    groupPolicy: false,
    toBeQuoted: false,
    directEmission: false,
    bankTransfer: false,
    disableNotifications: false,
    hasBlockingConditions: false,
    hasDiscount: false,
    blockingConditions: [],
    hasWarranties: false,
    variantWarranties: [],
    renewal: { automatic: false, to_be_confirmed: false },
    files: [
      {
        key: '',
        title: '',
      },
    ],
  });

  // Empty fields
  const [emptyFields, setEmptyFields] = useState({
    name: false,
    price: false,
    company: false,
    category: false,
    commission_value: false,
    management_price_value: false,
    api_identifier: false,
  });

  /**
   * Drag & Drop quotation fields
   */
  const [quotationOptions, setQuotationOptions] = useState(['1']);
  const sensorsQuotation = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEndQuotation(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setQuotationOptions((options) => {
        const oldIndex = options.indexOf(active.id);
        const newIndex = options.indexOf(over.id);

        const newOptions = arrayMove(options, oldIndex, newIndex);
        reorderState(quotationFields, setQuotationFields, newOptions);

        return newOptions;
      });
      setIsDirty(true);
    }
  }

  /**
   * Drag & Drop pricing rules
   */
  const [pricingRulesOptions, setPricingRulesOptions] = useState(['1']);
  const sensorsPricingRules = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEndPricingRules(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setPricingRulesOptions((options) => {
        const oldIndex = options.indexOf(active.id);
        const newIndex = options.indexOf(over.id);

        const newOptions = arrayMove(options, oldIndex, newIndex);
        reorderState(pricingRules, setPricingRules, newOptions);

        return newOptions;
      });

      setIsDirty(true);
    }
  }

  /**
   * Drag & Drop
   */
  const [warranties, setWarranties] = useState(['1']);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      setWarranties((warranties) => {
        const oldIndex = warranties.indexOf(active.id);
        const newIndex = warranties.indexOf(over.id);

        const newOptions = arrayMove(warranties, oldIndex, newIndex);
        reorderState(variantWarranties, setVariantWarranties, newOptions);

        return newOptions;
      });

      setIsDirty(true);
    }
  }

  // Fetch available automation options
  useEffect(() => {
    const fetchAllowedAutomations = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL ?? '/api'}/admin/automations/allowed`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
          },
        });
        const data = response.data;

        if (data.status === 'success') {
          const options = data.data.map((option: Automation) => ({
            label: option.name,
            value: option.name,
          }));

          setAutomationOptions(options);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchAllowedAutomations();
  }, []);

  /**
   * Fetch product data
   */
  useEffect(() => {
    async function fetchProductVariants() {
      try {
        setIsLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL ?? '/api'}/admin/products/${params.id}/variants`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('mb__access_token')}` },
          timeout: 10000,
        });
        const data = response.data;

        if (data.status === 'success') {
          setVariants(data.data);

          // Compare variants id with match.params.variantId and set states
          const matchingVariant: Variant = data.data.filter((variant: Variant) => variant._id === params.variantId)[0];
          if (matchingVariant) {
            setName(matchingVariant.name || '');
            setDescription(matchingVariant.description || '');
            setSelectedVariantType(matchingVariant.type || 'basic');
            setApiIdentifier(matchingVariant.api_id || '');
            setToBeQuoted(matchingVariant.to_be_quoted || false);
            setDirectEmission(matchingVariant.direct_emission || false);
            setBankTransfer(matchingVariant.bank_transfer || false);
            setDisableNotifications(matchingVariant.disable_notifications || false);

            // Group policy
            setGroupPolicy(matchingVariant?.group_policy || false);
            setGroupPolicyBroker(matchingVariant?.group_policy_metadata?.broker || '');
            setGroupPolicyBranch(matchingVariant?.group_policy_metadata?.branch || '');
            setGroupPolicyNumber(matchingVariant?.group_policy_metadata?.number || '');
            setGroupPolicyInclusionNumber(matchingVariant?.group_policy_metadata?.inclusion_number_prefix || '');

            // Individual sale
            setIndividualSale(matchingVariant?.individual_sale || false);

            // Email templates
            // setQuoteEmailTemplate(matchingVariant?.email_templates[0]?.value || '');
            // setPolicyEmailTemplate(matchingVariant?.email_templates[1]?.value || '');
            // setRenewalEmailTemplate(matchingVariant?.email_templates[2]?.value || '');

            setQuoteEmailTemplate(matchingVariant.email_templates && matchingVariant.email_templates[0] ? matchingVariant.email_templates[0].value : '');
            setPolicyEmailTemplate(matchingVariant.email_templates && matchingVariant.email_templates[1] ? matchingVariant.email_templates[1].value : '');
            setRenewalEmailTemplate(matchingVariant.email_templates && matchingVariant.email_templates[2] ? matchingVariant.email_templates[2].value : '');

            // Company
            setCompany(matchingVariant.company || null);
            setCompanyInputValue(matchingVariant.company ? matchingVariant.company.name : '');
            setSelectedCompanyOptions(matchingVariant.company ? [matchingVariant.company._id] : []);

            // Quotation fields
            setQuotationFields(matchingVariant.quotation_fields ?? []);
            setQuotationOptions(
              matchingVariant.quotation_fields
                ?.sort((a: QuotationField, b: QuotationField) => a.position - b.position)
                .map((field: QuotationField) => field.position.toString()) ?? [],
            );
            setQuotation(matchingVariant.quotation || false);

            // PricingTable
            setHasPricingTable(!!matchingVariant.pricing_table);
            setPricingTable(
              matchingVariant.pricing_table
                ? matchingVariant.pricing_table
                : { row: '', row_labels: [], column: '', column_labels: [], values: [], is_range: false },
            );
            setRows(matchingVariant.pricing_table ? matchingVariant.pricing_table.row : '');
            setColumns(matchingVariant.pricing_table ? matchingVariant.pricing_table.column : '');

            // Discount
            setHasDiscount(matchingVariant.has_discount || false);
            setHasDiscountPricingTable(!!matchingVariant.discount_pricing_table);
            setDiscountPricingTable(
              matchingVariant.discount_pricing_table
                ? matchingVariant.discount_pricing_table
                : { row: '', row_labels: [], column: '', column_labels: [], values: [], is_range: false },
            );
            setRowsDiscount(matchingVariant.discount_pricing_table ? matchingVariant.discount_pricing_table.row : '');
            setColumnsDiscount(matchingVariant.discount_pricing_table ? matchingVariant.discount_pricing_table.column : '');
            setDiscount({
              type: matchingVariant.discount ? matchingVariant.discount.type : 'fixed_amount',
              value: matchingVariant.discount ? matchingVariant.discount?.value?.toLocaleString('it-IT', { minimumFractionDigits: 2 }) : '0,00',
              has_enabling_field: matchingVariant.discount?.has_enabling_field ?? false,
              enabling_field: matchingVariant.discount?.enabling_field || {
                name: '',
                value: '',
                rule: RuleTypes.EQUAL,
              },
            });
            setHasEnablerFields(matchingVariant.discount?.has_enabling_field ?? false);
            setEnablerField(
              matchingVariant.discount?.enabling_field || {
                name: '',
                value: '',
                rule: RuleTypes.EQUAL,
              },
            );

            // Pricing rules
            setPricingRules(matchingVariant.pricing_rules ?? []);
            setPricingRulesOptions(
              matchingVariant.pricing_rules
                ?.sort((a: PricingRule, b: PricingRule) => a.position - b.position)
                .map((rule: PricingRule) => rule.position.toString()) ?? [],
            );
            setHasPricingRules(matchingVariant.pricing_rules ? matchingVariant.pricing_rules.length > 0 : false);

            // Convert price and salePrice from number to string with two decimal places and comma as decimalSeparator
            setPrice(matchingVariant.price ? matchingVariant.price.toLocaleString('it-IT', { minimumFractionDigits: 2 }) : '');

            // setHasOptions(matchingVariant.has_options || false);
            setHasWarranties(matchingVariant.warranties && matchingVariant.warranties.length > 0 ? true : false);
            setWarranties(
              matchingVariant.warranties?.sort((a: Warranty, b: Warranty) => a.position - b.position).map((warranty: Warranty) => String(warranty.position)) ??
                [],
            );
            setVariantWarranties(matchingVariant.warranties ? matchingVariant.warranties : []);

            // Commission
            setHasCommission(matchingVariant.commission ? true : false);
            setCommission({
              value: matchingVariant.commission ? Number(matchingVariant.commission.value).toLocaleString('it-IT', { minimumFractionDigits: 2 }) : '0,00',
              value_type: matchingVariant.commission ? matchingVariant.commission.value_type : 'fixed_amount',
            });

            // Management price
            setHasManagementPrice(matchingVariant.management_price ? true : false);
            setManagementPrice({
              value: matchingVariant.management_price ? matchingVariant.management_price.value.toLocaleString('it-IT', { minimumFractionDigits: 2 }) : '0,00',
              value_type: matchingVariant.management_price ? matchingVariant.management_price.value_type : 'fixed_amount',
            });

            // Renewal
            setRenewal(matchingVariant.renewal || { automatic: false, to_be_confirmed: false });

            // Shared Notification
            setHasSharedNotification(matchingVariant.shared_notification && matchingVariant.shared_notification.email_list.length > 0 ? true : false);
            setSelectedTags(
              matchingVariant.shared_notification && matchingVariant.shared_notification.email_list ? matchingVariant.shared_notification.email_list : [],
            );

            // Blocking conditions
            setHasBlockingConditions(matchingVariant.has_blocking_conditions ?? false);
            setBlockingConditions(matchingVariant.blocking_conditions ?? []);

            // Automation Bot
            setHasAutomationBot(matchingVariant.has_automation_bot || false);
            setAutomationName(matchingVariant.automation_name || '');

            // Files
            const fileArray = [];
            const fileNamesArray = [];
            const fileIdsArray = [];
            const fileOptions = [];
            const fileTypes = [];

            if (matchingVariant.files) {
              for (const item of matchingVariant.files) {
                fileArray.push('Document');
                fileNamesArray.push(item.title);
                fileIdsArray.push(item.key);
                let fileName = '';
                if (item.title.includes('__')) {
                  fileName = item.title.split('__')[1];
                } else {
                  fileName = item.title;
                }
                fileOptions.push({
                  label: `${fileName} - ${item.file_type ? parseVariantFileType(item.file_type) : ''}`,
                  value: item.key,
                });
                fileTypes.push(item.file_type);
              }
            }

            setOptionsFileUpload(fileOptions);

            // Set default state
            setDefaultState({
              name: matchingVariant.name || '',
              description: matchingVariant.description || '',
              groupPolicy: matchingVariant.group_policy || false,
              toBeQuoted: matchingVariant.to_be_quoted || false,
              directEmission: matchingVariant.direct_emission || false,
              bankTransfer: matchingVariant.bank_transfer || false,
              hasDiscount: matchingVariant.has_discount || false,
              disableNotifications: matchingVariant.disable_notifications || false,
              hasBlockingConditions: matchingVariant.has_blocking_conditions || false,
              // @ts-ignore
              blockingConditions: matchingVariant.blocking_conditions || [],
              price: matchingVariant.price ? matchingVariant.price.toLocaleString('it-IT', { minimumFractionDigits: 2 }) : '',
              hasWarranties: false,
              // matchingVariant.has_options || false,
              // @ts-ignore
              variantWarranties: matchingVariant.warranties || [],
              renewal: matchingVariant.renewal
                ? { automatic: matchingVariant.renewal.automatic ?? false, to_be_confirmed: matchingVariant.renewal.to_be_confirmed ?? false }
                : { automatic: false, to_be_confirmed: false },
              files: matchingVariant.files || [],
            });
          }

          setLoadError(false);
        } else {
          setLoadError(true);
        }
      } catch (err: any) {
        console.log(err);
        setLoadError(true);
      } finally {
        setIsLoading(false);
      }
    }
    async function fetchCompanies() {
      try {
        setCompanyLoading(true);
        const response = await axios.get((process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : '/api') + `/admin/companies`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('mb__access_token')}` },
          timeout: 10000,
        });
        const data = response.data;

        if (data.status === 'success') {
          const companies = data.data.map((company: Company) => {
            return {
              label: company.name,
              value: company._id,
              company: company,
            };
          });
          setCompanyOptions(companies);
          setDeselectedCompaniesOptions(companies);
        }
      } catch (err: any) {
        console.log(err);
      } finally {
        setCompanyLoading(false);
      }
    }
    fetchProductVariants().then(() => {
      fetchCompanies();
    });
  }, [update, params.id, user.token]);

  /**
   * Save data
   */
  const handleSave = useCallback(async () => {
    try {
      setButtonSpinning(true);

      // Check if fields are empty
      let errFlag = false;
      if (name === '') {
        setEmptyFields((emptyFields) => ({ ...emptyFields, name: true }));
        errFlag = true;
      }

      if (price === '' && !quotation && quotationFields.length === 0) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, price: true }));
        errFlag = true;
      }

      if (hasCommission && Number(commission.value.replace('.', '').replace(',', '.')) === 0) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, commission_value: true }));
        errFlag = true;
      }

      if (hasManagementPrice && Number(managementPrice.value.replace('.', '').replace(',', '.')) === 0) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, management_price_value: true }));
        errFlag = true;
      }

      if (selectedVariantType === 'api' && apiIdentifier === '') {
        setEmptyFields((emptyFields) => ({ ...emptyFields, api_identifier: true }));
        errFlag = true;
      }

      if (company === null) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, company: true }));
        errFlag = true;
      }

      if (errFlag) {
        setButtonSpinning(false);
        return;
      }

      // Create form data
      const formData = new FormData();

      // Files
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          console.log('file', files[i]);
          formData.append('documents', files[i]);
        }
      }

      // Pricing rules
      if (hasPricingRules) {
        pricingRules.forEach((rule: PricingRule, index: number) => {
          formData.append(`pricing_rules[${index}][name]`, rule.name);
          formData.append(`pricing_rules[${index}][type]`, rule.type);
          formData.append(`pricing_rules[${index}][value]`, rule.value);
          formData.append(`pricing_rules[${index}][position]`, String(rule.position));
        });
      }

      // Quotation fields
      if (quotationFields.length > 0) {
        quotationFields.forEach((field: QuotationField, index: number) => {
          formData.append(`quotation_fields[${index}][name]`, field.name);
          formData.append(`quotation_fields[${index}][type]`, field.type);
          formData.append(`quotation_fields[${index}][position]`, String(field.position));
          formData.append(`quotation_fields[${index}][result_modifier_type]`, field.result_modifier_type);

          if (field.result_modifier_value) formData.append(`quotation_fields[${index}][result_modifier_value]`, field.result_modifier_value);
          if (field.api_field_name) formData.append(`quotation_fields[${index}][api_field_name]`, field.api_field_name);
          if (field.values && field.values?.length > 0) {
            field.values.forEach((value: { label: string; value: string } | QuotationField, index2: number) => {
              // Used when type select is selected
              if (isLabelValuePair(value)) {
                formData.append(`quotation_fields[${index}][values][${index2}][label]`, value.label);
                formData.append(`quotation_fields[${index}][values][${index2}][value]`, value.value);
              } else {
                formData.append(`quotation_fields[${index}][values][${index2}][name]`, value.name);
                formData.append(`quotation_fields[${index}][values][${index2}][type]`, value.type);
                if (value.api_field_name) {
                  formData.append(`quotation_fields[${index}][values][${index2}][api_field_name]`, value.api_field_name);
                }
                if (value.result_modifier_type) {
                  formData.append(`quotation_fields[${index}][values][${index2}][result_modifier_type]`, value.result_modifier_type);
                }
                if (value.result_modifier_value) {
                  formData.append(`quotation_fields[${index}][values][${index2}][result_modifier_value]`, value.result_modifier_value);
                }
                if (value.type === 'select' && value.values) {
                  (
                    value.values as {
                      label: string;
                      value: string;
                    }[]
                  ).forEach((subValue: { label: string; value: string }, index3: number) => {
                    formData.append(`quotation_fields[${index}][values][${index2}][values][${index3}][label]`, subValue.label);
                    formData.append(`quotation_fields[${index}][values][${index2}][values][${index3}][value]`, subValue.value);
                  });
                }
              }
            });
          }
        });

        // Add pricing table if it exists
        if (hasPricingTable && pricingTable) {
          formData.append('pricing_table[row]', rows);
          formData.append('pricing_table[row_labels]', JSON.stringify(pricingTable.row_labels));
          formData.append('pricing_table[column]', columns);
          formData.append('pricing_table[column_labels]', JSON.stringify(pricingTable.column_labels));
          formData.append('pricing_table[values]', JSON.stringify(pricingTable.values));
          formData.append('pricing_table[is_range]', JSON.stringify(pricingTable.is_range));
        }
      }

      /**
       * Discount
       */
      formData.append('has_discount', hasDiscount ? 'true' : 'false');
      if (hasDiscount && discount && enablerField) {
        formData.append('discount[value]', String(discount.value).replace(',', '.'));
        formData.append('discount[type]', discount.type);
        formData.append('discount[has_enabling_field]', hasEnablerFields ? 'true' : 'false');
        formData.append('discount[enabling_field][name]', enablerField.name);
        formData.append('discount[enabling_field][value]', String(enablerField.value));
        formData.append('discount[enabling_field][rule]', enablerField.rule);
      }

      if (hasDiscount && hasDiscountPricingTable) {
        formData.append('discount_pricing_table[row]', rowsDiscount);
        formData.append('discount_pricing_table[row_labels]', JSON.stringify(discountPricingTable.row_labels));
        formData.append('discount_pricing_table[column]', columnsDiscount);
        formData.append('discount_pricing_table[column_labels]', JSON.stringify(discountPricingTable.column_labels));
        formData.append('discount_pricing_table[values]', JSON.stringify(discountPricingTable.values));
        formData.append('discount_pricing_table[is_range]', JSON.stringify(discountPricingTable.is_range));
      }

      // Variant warranties
      if (variantWarranties.length > 0) {
        variantWarranties.forEach((warranty: Warranty, index: number) => {
          formData.append(`warranties[${index}][name]`, warranty.name);
          formData.append(`warranties[${index}][included]`, warranty.included ? 'true' : 'false');
          formData.append(`warranties[${index}][result_modifier_type]`, warranty.result_modifier_type);
          if (warranty.result_modifier_value) formData.append(`warranties[${index}][result_modifier_value]`, warranty.result_modifier_value);
          formData.append(`warranties[${index}][value]`, warranty.value);
          formData.append(`warranties[${index}][position]`, String(warranty.position));
          formData.append(`warranties[${index}][description]`, warranty.description ?? '');
        });
      }
      formData.append('name', name);
      formData.append('description', description);
      formData.append('price', quotation ? '0' : price);
      formData.append('type', selectedVariantType);
      if (company?._id) {
        formData.append('company', company._id);
      }

      // Api identifier
      if (selectedVariantType === 'api') {
        formData.append('api_id', apiIdentifier);
      }

      formData.append('sale_price', quotation ? '0' : salePrice);
      formData.append('group_policy', String(groupPolicy));
      if (groupPolicy) {
        formData.append('group_policy_broker', groupPolicyBroker);
        formData.append('group_policy_branch', groupPolicyBranch);
        formData.append('group_policy_number', groupPolicyNumber);
        formData.append('group_policy_inclusion_number_prefix', groupPolicyInclusionNumber);
      }

      // Shared notification
      if (hasSharedNotification) {
        selectedTags.forEach((tag: string, index: number) => {
          formData.append(`shared_notification[email_list][${index}]`, tag);
        });
      }

      // Blocking conditions
      formData.append('has_blocking_conditions', String(hasBlockingConditions));
      if (hasBlockingConditions) {
        blockingConditions.forEach((condition: TBlockingCondition, index: number) => {
          formData.append(`blocking_conditions[${index}][field]`, condition.field);
          condition.conditions.forEach((cond: TCondition, index2: number) => {
            formData.append(`blocking_conditions[${index}][conditions][${index2}][rule]`, cond.rule);
            formData.append(`blocking_conditions[${index}][conditions][${index2}][value]`, cond.value);
          });
        });
      }

      // Automation bot
      formData.append('has_automation_bot', String(hasAutomationBot));
      if (hasAutomationBot) {
        formData.append('automation_name', automationName);
      }

      // Individual sale
      formData.append('individual_sale', String(individualSale));

      // Email templates
      formData.append(`email_templates[0][label]`, 'quote');
      formData.append(`email_templates[0][value]`, quoteEmailTemplate);
      formData.append(`email_templates[1][label]`, 'policy');
      formData.append(`email_templates[1][value]`, policyEmailTemplate);
      formData.append(`email_templates[2][label]`, 'renewal');
      formData.append(`email_templates[2][value]`, renewalEmailTemplate);

      formData.append('to_be_quoted', String(toBeQuoted));
      formData.append('direct_emission', String(directEmission));
      formData.append('bank_transfer', String(bankTransfer));
      formData.append('disable_notifications', String(disableNotifications));
      formData.append('quotation', String(quotation));
      hasCommission && formData.append('commission', JSON.stringify(commission));
      hasManagementPrice && formData.append('management_price', JSON.stringify(managementPrice));

      // Renewal
      formData.append('renewal[automatic]', String(renewal.automatic));
      formData.append('renewal[to_be_confirmed]', String(renewal.to_be_confirmed));

      const response = await axios.put(`${process.env.REACT_APP_API_URL ?? '/api'}/admin/products/${params.id}/variants/${params.variantId}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
        },
      });
      const data = response.data;

      if (data.status === 'success') {
        // Clear files
        setFiles([]);

        setActive(true);
        setUpdate(!update);
        setIsDirty(false);
      } else {
        setSaveError(true);
      }
    } catch (error) {
      console.log(error);
      setSaveError(true);
    } finally {
      setButtonSpinning(false);
    }
  }, [
    update,
    name,
    description,
    company,
    selectedVariantType,
    apiIdentifier,
    price,
    salePrice,
    groupPolicy,
    groupPolicyBroker,
    groupPolicyBranch,
    groupPolicyNumber,
    groupPolicyInclusionNumber,
    hasSharedNotification,
    hasBlockingConditions,
    blockingConditions,
    hasAutomationBot,
    automationName,
    selectedTags,
    toBeQuoted,
    bankTransfer,
    disableNotifications,
    directEmission,
    individualSale,
    quoteEmailTemplate,
    policyEmailTemplate,
    renewalEmailTemplate,
    variantWarranties,
    quotation,
    quotationFields,
    pricingTable,
    discount,
    hasEnablerFields,
    discountPricingTable,
    enablerField,
    hasPricingRules,
    pricingRules,
    hasCommission,
    commission,
    hasManagementPrice,
    managementPrice,
    renewal,
    files,
  ]);

  /**
   * Policy Handlers
   */
  const handleNameChange = useCallback(
    (e: string) => {
      // If emptyFields.name is true, set it to false
      if (emptyFields.name) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, name: false }));
      }
      setName(e);
      setIsDirty(true);
    },
    [emptyFields.name],
  );

  // Description
  const handleDescriptionChange = useCallback((e: string) => {
    setDescription(e);
    setIsDirty(true);
  }, []);

  // Variant type
  const handleVariantTypeChange = useCallback((e: any) => {
    setSelectedVariantType(e);
    setIsDirty(true);
  }, []);

  // Api identifier handler
  const handleApiIdentifierChange = useCallback(
    (e: string) => {
      // If emptyFields.api_identifier is true, set it to false
      if (emptyFields.api_identifier) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, api_identifier: false }));
      }

      setApiIdentifier(e);
      setIsDirty(true);
    },
    [apiIdentifier, emptyFields.api_identifier],
  );

  // Group policy
  const handleGroupPolicyChange = useCallback(() => {
    setGroupPolicy(!groupPolicy);
    setIsDirty(true);
  }, [groupPolicy]);

  // Group policy broker
  const handleGroupPolicyBrokerChange = useCallback((e: string) => {
    setGroupPolicyBroker(e);
    setIsDirty(true);
  }, []);

  // Group policy branch
  const handleGroupPolicyBranchChange = useCallback((e: string) => {
    setGroupPolicyBranch(e);
    setIsDirty(true);
  }, []);

  // Group policy number
  const handleGroupPolicyNumberChange = useCallback((e: string) => {
    setGroupPolicyNumber(e);
    setIsDirty(true);
  }, []);

  // Group policy inclusion number
  const handleGroupPolicyInclusionNumberChange = useCallback((e: string) => {
    setGroupPolicyInclusionNumber(e);
    setIsDirty(true);
  }, []);

  // To be quoted
  const handleToBeQuotedChange = useCallback(() => {
    setToBeQuoted(!toBeQuoted);
    setIsDirty(true);
  }, [toBeQuoted]);

  // Bank Transfer
  const handleBankTransferChange = useCallback(() => {
    setBankTransfer(!bankTransfer);
    setIsDirty(true);
  }, [bankTransfer]);

  // Disable notifications
  const handleDisableNotificationsChange = useCallback(() => {
    setDisableNotifications(!disableNotifications);
    setIsDirty(true);
  }, [disableNotifications]);

  // Direct emission
  const handleDirectEmissionChange = useCallback(() => {
    setDirectEmission(!directEmission);
    setIsDirty(true);
  }, [directEmission]);

  // Individual sale
  const handleIndividualSaleChange = useCallback(() => {
    setIndividualSale(!individualSale);
    setIsDirty(true);
  }, [individualSale]);

  // Quote email template
  const handleQuoteEmailTemplateChange = useCallback((e: any) => {
    setQuoteEmailTemplate(e);
    setIsDirty(true);
  }, []);

  // Policy email template
  const handlePolicyEmailTemplateChange = useCallback((e: any) => {
    setPolicyEmailTemplate(e);
    setIsDirty(true);
  }, []);

  // Renewal email template
  const handleRenewalEmailTemplateChange = useCallback((e: any) => {
    setRenewalEmailTemplate(e);
    setIsDirty(true);
  }, []);

  // Quotation
  const handleQuotationChange = useCallback(
    (e: any) => {
      setQuotation(e);
      setIsDirty(true);
    },
    [quotation],
  );

  // Discount
  const handleHasDiscountChange = useCallback(() => {
    setHasDiscount((prev) => !prev);
    setIsDirty(true);
  }, []);

  const handleDiscountTypeChange = useCallback((value: string) => {
    setDiscount((prev: any) => ({ ...prev, type: value as Discount['type'] }));
    setIsDirty(true);
  }, []);

  const handleDiscountValueChange = useCallback((value: string) => {
    setDiscount((prev: any) => ({ ...prev, value }));
    setIsDirty(true);
  }, []);

  //Enabler Field
  const handleHasEnablerFieldChange = useCallback(() => {
    if (!hasEnablerFields) {
      setEnablerField({
        name: '',
        value: '',
        rule: RuleTypes.EQUAL,
      });
    }
    setHasEnablerFields(!hasEnablerFields);
    setIsDirty(true);
  }, [hasEnablerFields]);

  const handleEnablerFieldChange = useCallback(
    (name: string) => {
      const newEnablerField = { ...enablerField };
      newEnablerField.name = name;
      setEnablerField(newEnablerField);
      setIsDirty(true);
    },
    [enablerField],
  );

  const handleEnableFieldValueChange = useCallback(
    (value: string) => {
      const newEnablerField = { ...enablerField };
      newEnablerField.value = value;
      setEnablerField(newEnablerField);
      setIsDirty(true);
    },
    [enablerField],
  );

  const handleEnableFieldRuleChange = useCallback(
    (rule: RuleTypes) => {
      const newEnablerField = { ...enablerField };
      newEnablerField.rule = rule;
      setEnablerField(newEnablerField);
      setIsDirty(true);
    },
    [enablerField],
  );

  // Blocking conditions
  const handleHasBlockingConditionsChange = useCallback(() => {
    // If set to true, populate the blocking conditions array with a default object
    if (!hasBlockingConditions && blockingConditions.length === 0) {
      setBlockingConditions([
        {
          field: '',
          conditions: [{ rule: RuleTypes.EQUAL, value: '' }],
        },
      ]);
    }
    setHasBlockingConditions(!hasBlockingConditions);
    setIsDirty(true);
  }, [hasBlockingConditions]);

  const handleBlockingConditionFieldChange = useCallback(
    (index: number, field: string) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].field = field;
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleBlockingConditionValueChange = useCallback(
    (index: number, conditionIndex: number, value: string) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].conditions[conditionIndex].value = value;
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleBlockingConditionRuleChange = useCallback(
    (index: number, conditionIndex: number, rule: RuleTypes) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].conditions[conditionIndex].rule = rule;
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleAddBlockingCondition = useCallback(() => {
    setBlockingConditions((prevConditions) => [
      ...prevConditions,
      {
        field: '',
        conditions: [{ rule: RuleTypes.EQUAL, value: '' }],
      },
    ]);
    setIsDirty(true);
  }, []);

  const handleAddBlockingConditionRule = useCallback(
    (index: number) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].conditions.push({ rule: RuleTypes.EQUAL, value: '' });
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleDeleteBlockingCondition = useCallback(
    (index: number) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions.splice(index, 1);
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  const handleDeleteBlockingConditionRule = useCallback(
    (index: number, subIndex: number) => {
      const newBlockingConditions = [...blockingConditions];
      newBlockingConditions[index].conditions.splice(subIndex, 1);
      setBlockingConditions(newBlockingConditions);
      setIsDirty(true);
    },
    [blockingConditions],
  );

  // Is pricing range
  const handlePricingRangeChange = useCallback((e: any) => {
    setPricingTable((prevTable) => ({ ...prevTable, is_range: e }));
    setIsDirty(true);
  }, []);

  const handlePriceChange = useCallback(
    (e: any) => {
      // If emptyFields.price is true, set it to false
      if (emptyFields.price) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, price: false }));
      }

      // Check if the price has a dot and replace it with a comma
      if (e.includes('.')) {
        e = e.replace('.', ',');
      }

      // Check if the price has 2 or more commas and remove the last one
      if (e.split(',').length > 2) {
        e = e.replace(/,(?=[^,]*$)/, '');
      }

      // Check if the price has 3 or more numbers after the comma and remove the last one
      if (e.split(',')[1] && e.split(',')[1].length > 2) {
        e = e.replace(/.$/, '');
      }

      // Check if there are letters or special characters and remove them
      if (e.match(/[^0-9,]/g)) {
        e = e.replace(/[^0-9,]/g, '');
      }

      setPrice(e);
      setIsDirty(true);
    },
    [emptyFields.price],
  );

  const handleSalePriceChange = useCallback((e: any) => {
    // Check if the price has a dot and replace it with a comma
    if (e.includes('.')) {
      e = e.replace('.', ',');
    }

    // Check if the price has 2 or more commas and remove the last one
    if (e.split(',').length > 2) {
      e = e.replace(/,(?=[^,]*$)/, '');
    }

    // Check if the price has 3 or more numbers after the comma and remove the last one
    if (e.split(',')[1] && e.split(',')[1].length > 2) {
      e = e.replace(/.$/, '');
    }

    // Check if there are letters or special characters and remove them
    if (e.match(/[^0-9,]/g)) {
      e = e.replace(/[^0-9,]/g, '');
    }

    setSalePrice(e);
    setIsDirty(true);
  }, []);

  /** Pricing rules */
  const handleHasPricingRulesChange = useCallback(() => {
    setHasPricingRules(!hasPricingRules);
    setIsDirty(true);
  }, [hasPricingRules]);

  /** Commission */
  const handleHasCommissionChange = useCallback(() => {
    setHasCommission(!hasCommission);
    setIsDirty(true);
  }, [hasCommission]);

  const handleCommissionValueChange = useCallback(
    (e: any) => {
      // If emptyFields.commission_value is true, set it to false
      if (emptyFields.commission_value) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, commission_value: false }));
      }

      // Check if the price has 2 or more commas and remove the last one
      if (e.split(',').length > 2) {
        e = e.replace(/,(?=[^,]*$)/, '');
      }

      // Check if there are letters or special characters and remove them
      if (e.match(/[^0-9,]/g)) {
        e = e.replace(/[^0-9,]/g, '');
      }

      // If commission type is percentage, check if the value is greater than 100
      if (commission.value_type === 'percentage' && parseFloat(e.replace(',', '.')) > 100) {
        e = '100';
      }

      setCommission({ ...commission, value: e });
      setIsDirty(true);
    },
    [commission, emptyFields.commission_value],
  );

  const handleCommissionTypeChange = useCallback(
    (e: any) => {
      setCommission({ ...commission, value_type: e });
      setIsDirty(true);
    },
    [commission],
  );

  /** Management Price */
  const handleHasManagementPriceChange = useCallback(() => {
    setHasManagementPrice(!hasManagementPrice);
    setIsDirty(true);
  }, [hasManagementPrice]);

  const handleManagementPriceValueChange = useCallback(
    (e: any) => {
      // If emptyFields.management_price_value is true, set it to false
      if (emptyFields.management_price_value) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, management_price_value: false }));
      }

      // Check if the price has 2 or more commas and remove the last one
      if (e.split(',').length > 2) {
        e = e.replace(/,(?=[^,]*$)/, '');
      }

      // Check if there are letters or special characters and remove them
      if (e.match(/[^0-9,]/g)) {
        e = e.replace(/[^0-9,]/g, '');
      }

      // If commission type is percentage, check if the value is greater than 100
      if (managementPrice.value_type === 'percentage' && parseFloat(e.replace(',', '.')) > 100) {
        e = '100';
      }

      setManagementPrice({ ...managementPrice, value: e });
      setIsDirty(true);
    },
    [managementPrice, emptyFields.management_price_value],
  );

  const handleManagementPriceTypeChange = useCallback(
    (e: any) => {
      setManagementPrice({ ...managementPrice, value_type: e });
      setIsDirty(true);
    },
    [managementPrice],
  );

  /** Shared notification */
  const handleHasSharedNotificationChange = useCallback(() => {
    setHasSharedNotification(!hasSharedNotification);
    setIsDirty(true);
  }, [hasSharedNotification]);

  /** Automation Bot */
  const handleHasAutomationBotChange = useCallback(() => {
    setHasAutomationBot(!hasAutomationBot);
    setIsDirty(true);
  }, [hasAutomationBot]);

  const handleAutomationSelectChange = useCallback((e: string) => {
    console.log(e);
    setAutomationName(e);
    setIsDirty(true);
  }, []);

  const handleHasWarrantiesChange = useCallback(() => {
    setHasWarranties(!hasWarranties);
    setIsDirty(true);
  }, [hasWarranties]);

  /** Validate email */
  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      );
  };

  /**
   * Add warranty option
   */
  const handleAddWarrantyOption = useCallback(() => {
    setWarranties((warranties) => [...warranties, String(warranties.length + 1)]);
  }, []);

  /**
   * Remove warranty option
   */
  const handleRemoveWarranty = useCallback(
    (id: string) => {
      setWarranties((warranties) => warranties.filter((warranty) => warranty !== id));
      // Remove the warranty from the variantWarranties array if it exists
      setVariantWarranties((variantWarranties) => {
        const newVariantOptions = variantWarranties.filter((warranty) => String(warranty.position) !== id);
        return newVariantOptions;
      });
    },
    [setWarranties],
  );

  /**
   * Add variant option
   */
  const handleAddVariantWarranty = useCallback(
    (
      position: string,
      name: string,
      value: string,
      included: boolean,
      deductible: string,
      result_modifier_type: 'fixed_amount' | 'percentage' | 'none',
      result_modifier_value?: string,
      description?: string,
    ) => {
      setVariantWarranties((variantWarranties) => [
        ...variantWarranties,
        {
          name: name,
          value: value,
          position: Number(position),
          result_modifier_type: result_modifier_type,
          included: included,
          deductible: deductible,
          result_modifier_value: result_modifier_value,
          description: description,
        },
      ]);
      setIsDirty(true);
    },
    [],
  );

  /**
   * Update variant option
   */
  const handleUpdateVariantWarranty = useCallback(
    (
      id: string,
      position: string,
      name: string,
      value: string,
      included: boolean,
      deductible: string,
      result_modifier_type: 'fixed_amount' | 'percentage' | 'none',
      result_modifier_value?: string,
      description?: string,
    ) => {
      const tmp = variantWarranties;
      const index = tmp.findIndex((warranty: any) => String(warranty._id) === id);
      console.log(description);
      tmp[index] = {
        name: name,
        value: value,
        position: Number(position),
        result_modifier_type: result_modifier_type,
        included: included,
        deductible: deductible,
        result_modifier_value: result_modifier_value,
        description: description,
      };

      setVariantWarranties(tmp);
      setIsDirty(true);
    },
    [variantWarranties],
  );

  /**
   * Warranty markup
   */
  const warrantyMarkup = (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={warranties} strategy={verticalListSortingStrategy}>
          {warranties.map((id, index) => (
            <SortableItemWarranty
              key={`${variantWarranties[index]?.name ?? 'warranty'}-${id}`}
              id={id}
              warrantyItem={variantWarranties[index]}
              options={variantWarrantiesModifierType}
              onDelete={handleRemoveWarranty}
              onSave={handleAddVariantWarranty}
              onUpdate={handleUpdateVariantWarranty}
            />
          ))}
        </SortableContext>
      </DndContext>
      <LegacyCard.Section>
        <Button plain removeUnderline icon={PlusMinor as any} onClick={handleAddWarrantyOption}>
          Aggiungi garanzia
        </Button>
      </LegacyCard.Section>
    </>
  );

  /**
   * Add quotation option
   */
  const handleAddQuotationOption = useCallback(() => {
    setQuotationOptions((options) => [...options, String(options.length + 1)]);
  }, []);

  /**
   * Remove quotation field
   */
  const handleRemoveQuotationField = useCallback(
    (id: string) => {
      setQuotationOptions((options) => options.filter((option) => option !== id));
      // Remove the option from the quotationFields array if it exists
      const tmp = quotationFields;
      const index = tmp.findIndex((option) => String(option.position) === id);
      tmp.splice(index, 1);
      setQuotationFields(tmp);
      setIsDirty(true);
    },
    [setQuotationOptions, quotationFields],
  );

  /**
   * Add quotation field
   */
  const handleAddQuotationField = useCallback(
    (
      position: string,
      name: string,
      type: 'select' | 'percentage' | 'number' | 'boolean' | 'text' | 'currency' | 'array' | 'date',
      result_modifier_type: 'fixed_amount' | 'percentage' | 'pricing_table' | 'none',
      // values?: Array<{ label: string; value: string }>,
      values?: any,
      result_modifier_value?: string,
      api_field_name?: string,
    ) => {
      // If values is not empty, convert it to a JSON array and check if each object has a label and a value
      let tmp: Array<{ label: string; value: string }> = [];
      if (values) {
        try {
          if (typeof values === 'string') {
            tmp = JSON.parse(values);
            tmp.forEach((value) => {
              if (!isLabelValuePair(value)) {
                throw new Error('Invalid values');
              }
            });
          } else if (Array.isArray(values)) {
            tmp = values;
          }
        } catch (err) {
          console.log(err);
          return;
        }
      }

      setQuotationFields((quotationFields) => [
        ...quotationFields,
        {
          name: name,
          type: type,
          values: tmp,
          position: Number(position),
          result_modifier_type: result_modifier_type,
          result_modifier_value: result_modifier_value,
          api_field_name: api_field_name,
        },
      ]);
      setIsDirty(true);
    },
    [],
  );

  // Define a type guard for { label: string; value: string; }
  function isLabelValuePair(value: any): value is { label: string; value: string } {
    return value && typeof value.label === 'string' && typeof value.value === 'string' && value.label !== '' && value.value !== '';
  }

  /**
   * Update quotation field
   */
  const handleUpdateQuotationField = useCallback(
    (
      _id: string,
      position: string,
      name: string,
      type: 'select' | 'percentage' | 'number' | 'boolean' | 'text' | 'currency' | 'array' | 'date',
      result_modifier_type: 'fixed_amount' | 'percentage' | 'pricing_table' | 'none',
      values?: any,
      result_modifier_value?: string,
      api_field_name?: string,
    ) => {
      const tmp = quotationFields;
      const index = tmp.findIndex((field: any) => field._id === _id);
      // If values is not empty, convert it to a JSON array and check if each object has a label and a value
      let tmpValues: Array<{ label: string; value: string }> | Array<QuotationField> = [];
      if (values) {
        try {
          if (typeof values === 'string') {
            tmpValues = JSON.parse(values);
            tmpValues.forEach((value) => {
              if (!isLabelValuePair(value)) {
                throw new Error('Invalid values');
              }
            });
          } else if (Array.isArray(values)) {
            tmpValues = values;
          }
        } catch (err) {
          console.log(err);
          return;
        }
      }
      tmp[index] = {
        name: name,
        position: Number(position),
        type: type,
        result_modifier_type: result_modifier_type,
        result_modifier_value: result_modifier_value,
        values: tmpValues,
        api_field_name: api_field_name,
      };

      setQuotationFields(tmp);
      setIsDirty(true);
    },
    [quotationFields],
  );

  /**
   * Quotation markup
   */
  const quotationMarkup = (
    <>
      <DndContext sensors={sensorsQuotation} collisionDetection={closestCenter} onDragEnd={handleDragEndQuotation}>
        <SortableContext items={quotationOptions} strategy={verticalListSortingStrategy}>
          {quotationOptions.map((id, index) => (
            <SortableItemQuotation
              key={`${quotationFields[index]?.name ?? 'quotationField'}-${index}`}
              id={id}
              quotationItem={quotationFields[index]}
              options={quotationFieldsType}
              onDelete={handleRemoveQuotationField}
              onSave={handleAddQuotationField}
              onUpdate={handleUpdateQuotationField}
              hasPricingTable={hasPricingTable}
              setHasPricingTable={setHasPricingTable}
            />
          ))}
        </SortableContext>
      </DndContext>
      <LegacyCard.Section>
        <Button plain removeUnderline icon={PlusMinor as any} onClick={handleAddQuotationOption}>
          Aggiungi campo quotazione
        </Button>
      </LegacyCard.Section>
    </>
  );

  /**
   * Add pricing rule option
   */
  const handleAddPricingRulesOption = useCallback(() => {
    setPricingRulesOptions((options) => [...options, String(options.length + 1)]);
  }, []);

  /**
   * Remove quotation field
   */
  const handleRemovePricingRule = useCallback(
    (id: string) => {
      setPricingRulesOptions((options) => options.filter((option) => option !== id));
      // Remove the option from the pricingRules array if it exists
      const tmp = pricingRules;
      const index = tmp.findIndex((option) => String(option.position) === id);
      tmp.splice(index, 1);
      setPricingRules(tmp);
      setIsDirty(true);
    },
    [setPricingRulesOptions, pricingRules],
  );

  /**
   * Add quotation field
   */
  const handleAddPricingRule = useCallback((position: string, name: 'minimum_price', type: 'fixed_amount' | 'percentage', value: string) => {
    setPricingRules((pricingRules) => [...pricingRules, { name: name, type: type, value: value, position: Number(position) }]);
    setIsDirty(true);
  }, []);

  /**
   * Update quotation field
   */
  const handleUpdatePricingRule = useCallback(
    (_id: string, position: string, name: 'minimum_price', type: 'fixed_amount' | 'percentage', value: string) => {
      const tmp = pricingRules;
      const index = tmp.findIndex((field: any) => field._id === _id);
      tmp[index] = { name: name, type: type, value: value, position: Number(position) };

      setPricingRules(tmp);
      setIsDirty(true);
    },
    [pricingRules],
  );

  /**
   * Pricing table
   */

  // State to manage the pricing table input fields
  // We will identify the input fields by their index from the table by the formula (number of columns * row index + column index))
  // Update the pricing table values
  const handleUpdatePricingTableValues = useCallback(
    (values: number[][]) => {
      const tmp = pricingTable;
      tmp.values = values;
      setPricingTable({ ...pricingTable, values: values });
      setIsDirty(true);
    },
    [pricingTable],
  );

  // Add Pricing Table
  const handleAddPricingTable = useCallback(() => {
    const row = quotationFields.find((field) => {
      if (rows?.includes('.') && Array.isArray(field.values)) {
        return field.values.find((subValue) => {
          return (subValue as QuotationField).api_field_name === rows;
        });
      } else {
        return field.api_field_name === rows;
      }
    });

    const result_modifier_value_row =
      rows?.includes('.') && Array.isArray(row?.values)
        ? row?.values.find((subValue): subValue is QuotationField => (subValue as QuotationField).api_field_name === rows)?.result_modifier_value
        : row?.result_modifier_value;

    const column = quotationFields.find((field) => {
      if (columns?.includes('.') && Array.isArray(field.values)) {
        return field.values.find((subValue) => {
          return (subValue as QuotationField).api_field_name === columns;
        });
      } else {
        return field.api_field_name === columns;
      }
    });

    const result_modifier_value_column =
      columns?.includes('.') && Array.isArray(column?.values)
        ? column?.values.find((subValue): subValue is QuotationField => (subValue as QuotationField).api_field_name === columns)?.result_modifier_value
        : column?.result_modifier_value;

    if (!result_modifier_value_row || !result_modifier_value_column) {
      return;
    }
    // convert the values to an array of strings
    const rowLabelsArray = result_modifier_value_row?.split(',').map((rowLabel) => rowLabel.trim());
    const columnLabelsArray = result_modifier_value_column?.split(',').map((columnLabel) => columnLabel.trim());

    // set the intial state of the table as a matrix of zeros, with dimensions equal to the number of rows and columns
    const valuesTmp: Array<Array<number>> = [];
    for (let i = 0; i < rowLabelsArray.length; i++) {
      valuesTmp.push([]);
      for (let j = 0; j < columnLabelsArray.length; j++) {
        valuesTmp[i].push(0);
      }
    }
    setPricingTable((prevTable) => ({
      ...prevTable,
      row: rows,
      row_labels: rowLabelsArray,
      column: columns,
      column_labels: columnLabelsArray,
      values: valuesTmp,
    }));

    // disable the creation of more tables
    setPricingTableExists(true);
    setIsDirty(true);
  }, [quotation, pricingTable, quotationFields, rows, columns]);

  // Update pricing table
  const handleUpdatePricingTable = useCallback(() => {
    const row = quotationFields.find((field) => {
      if (rows?.includes('.') && Array.isArray(field.values)) {
        return field.values.find((subValue) => {
          return (subValue as QuotationField).api_field_name === rows;
        });
      } else {
        return field.api_field_name === rows;
      }
    });

    const result_modifier_value_row =
      rows?.includes('.') && Array.isArray(row?.values)
        ? row?.values.find((subValue): subValue is QuotationField => (subValue as QuotationField).api_field_name === rows)?.result_modifier_value
        : row?.result_modifier_value;

    const column = quotationFields.find((field) => {
      if (columns?.includes('.') && Array.isArray(field.values)) {
        return field.values.find((subValue) => {
          return (subValue as QuotationField).api_field_name === columns;
        });
      } else {
        return field.api_field_name === columns;
      }
    });

    const result_modifier_value_column =
      columns?.includes('.') && Array.isArray(column?.values)
        ? column?.values.find((subValue): subValue is QuotationField => (subValue as QuotationField).api_field_name === columns)?.result_modifier_value
        : column?.result_modifier_value;

    if (!result_modifier_value_row || !result_modifier_value_column) {
      return;
    }

    const rowLabelsArray = result_modifier_value_row.split(',').map((rowLabel) => rowLabel.trim());
    const columnLabelsArray = result_modifier_value_column.split(',').map((columnLabel) => columnLabel.trim());

    // Create an empty matrix with the dimensions of the pricing table
    const updatedState: Array<Array<number>> = [];
    for (let i = 0; i < rowLabelsArray.length; i++) {
      updatedState.push([]);
      for (let j = 0; j < columnLabelsArray.length; j++) {
        updatedState[i].push(0);
      }
    }
    setPricingTable((prevTable) => ({
      ...prevTable,
      row: rows,
      row_labels: rowLabelsArray,
      column: columns,
      column_labels: columnLabelsArray,
      values: updatedState,
    }));
    setIsDirty(true);
  }, [quotation, pricingTable, quotationFields, rows, columns]);

  // Remove pricing table
  const handleRemovePricingTable = useCallback(() => {
    setPricingTableExists(false);
    setPricingTable({ row: '', row_labels: [], column: '', column_labels: [], values: [], is_range: false });
    setRows('');
    setColumns('');
    setIsDirty(true);
  }, []);

  /**
   * Discount pricing table
   */

  // Discount pricing table
  const handleUpdateDiscountPricingTableValues = useCallback(
    (values: number[][]) => {
      const tmp = discountPricingTable;
      tmp.values = values;
      setDiscountPricingTable({ ...discountPricingTable, values: values });
      setIsDirty(true);
    },
    [discountPricingTable],
  );

  // Add Discount Pricing Table
  const handleAddDiscountPricingTable = useCallback(() => {
    const row = quotationFields?.find((field) => {
      if (rowsDiscount?.includes('.') && Array.isArray(field.values)) {
        return field.values.find((subValue) => {
          return (subValue as QuotationField).api_field_name === rowsDiscount;
        });
      } else {
        return field.api_field_name === rowsDiscount;
      }
    });

    const result_modifier_value_row =
      rowsDiscount?.includes('.') && Array.isArray(row?.values)
        ? row?.values.find((subValue): subValue is QuotationField => (subValue as QuotationField).api_field_name === rowsDiscount)?.result_modifier_value
        : row?.result_modifier_value;

    const column = quotationFields.find((field) => {
      if (columnsDiscount?.includes('.') && Array.isArray(field.values)) {
        return field.values.find((subValue) => {
          return (subValue as QuotationField).api_field_name === columnsDiscount;
        });
      } else {
        return field.api_field_name === columnsDiscount;
      }
    });

    const result_modifier_value_column =
      columnsDiscount?.includes('.') && Array.isArray(column?.values)
        ? column?.values.find((subValue): subValue is QuotationField => (subValue as QuotationField).api_field_name === columnsDiscount)?.result_modifier_value
        : column?.result_modifier_value;

    if (!result_modifier_value_row || !result_modifier_value_column) {
      return;
    }
    // convert the values to an array of strings
    const rowLabelsArray = result_modifier_value_row?.split(',').map((rowLabel) => rowLabel.trim());
    const columnLabelsArray = result_modifier_value_column?.split(',').map((columnLabel) => columnLabel.trim());

    // set the intial state of the table as a matrix of zeros, with dimensions equal to the number of rows and columns
    const valuesTmp: Array<Array<number>> = [];
    for (let i = 0; i < rowLabelsArray.length; i++) {
      valuesTmp.push([]);
      for (let j = 0; j < columnLabelsArray.length; j++) {
        valuesTmp[i].push(0);
      }
    }
    setDiscountPricingTable((prevTable) => ({
      ...prevTable,
      row: rowsDiscount,
      row_labels: rowLabelsArray,
      column: columnsDiscount,
      column_labels: columnLabelsArray,
      values: valuesTmp,
    }));

    // disable the creation of more tables
    setHasDiscountPricingTable(true);
    setIsDirty(true);
  }, [quotation, discountPricingTable, quotationFields, rowsDiscount, columnsDiscount]);

  // Update pricing table
  const handleUpdateDiscountPricingTable = useCallback(() => {
    const row = quotationFields.find((field) => {
      if (rowsDiscount?.includes('.') && Array.isArray(field.values)) {
        return field.values.find((subValue) => {
          return (subValue as QuotationField).api_field_name === rowsDiscount;
        });
      } else {
        return field.api_field_name === rowsDiscount;
      }
    });

    const result_modifier_value_row =
      rowsDiscount?.includes('.') && Array.isArray(row?.values)
        ? row?.values.find((subValue): subValue is QuotationField => (subValue as QuotationField).api_field_name === rowsDiscount)?.result_modifier_value
        : row?.result_modifier_value;

    const column = quotationFields.find((field) => {
      if (columnsDiscount?.includes('.') && Array.isArray(field.values)) {
        return field.values.find((subValue) => {
          return (subValue as QuotationField).api_field_name === columnsDiscount;
        });
      } else {
        return field.api_field_name === columnsDiscount;
      }
    });

    const result_modifier_value_column =
      columnsDiscount?.includes('.') && Array.isArray(column?.values)
        ? column?.values.find((subValue): subValue is QuotationField => (subValue as QuotationField).api_field_name === columnsDiscount)?.result_modifier_value
        : column?.result_modifier_value;

    if (!result_modifier_value_row || !result_modifier_value_column) {
      return;
    }

    const rowLabelsArray = result_modifier_value_row.split(',').map((rowLabel) => rowLabel.trim());
    const columnLabelsArray = result_modifier_value_column.split(',').map((columnLabel) => columnLabel.trim());

    // Create an empty matrix with the dimensions of the pricing table
    const updatedState: Array<Array<number>> = [];
    for (let i = 0; i < rowLabelsArray.length; i++) {
      updatedState.push([]);
      for (let j = 0; j < columnLabelsArray.length; j++) {
        updatedState[i].push(0);
      }
    }
    setDiscountPricingTable((prevTable) => ({
      ...prevTable,
      row: rows,
      row_labels: rowLabelsArray,
      column: columns,
      column_labels: columnLabelsArray,
      values: updatedState,
    }));
    setIsDirty(true);
  }, [quotation, discountPricingTable, quotationFields, rowsDiscount, columnsDiscount]);

  // Remove pricing table
  const handleRemoveDiscountPricingTable = useCallback(() => {
    setHasDiscountPricingTable(false);
    setDiscountPricingTable({ row: '', row_labels: [], column: '', column_labels: [], values: [], is_range: false });
    setRowsDiscount('');
    setColumnsDiscount('');
    setIsDirty(true);
  }, []);

  /**
   * Handle file upload
   */
  const handleTmpFileUploadValue = useCallback((e: any) => {
    setTmpFileUploadValue(e);
  }, []);

  const toggleModalFileUpload = useCallback(() => setActiveFileUpload((activeFileUpload: boolean) => !activeFileUpload), []);
  const toggleIsAdderFileUpload = useCallback((e: boolean) => {
    setIsAdderFileUpload((isAdderFileUpload) => {
      isAdderFileUpload = e;
      return isAdderFileUpload;
    });
  }, []);

  const handleFileUploadAdd = useCallback(
    async (e: any, fn: string, type: VariantFileType) => {
      if (e === '' || e === undefined || fn === '' || fn === undefined || type === undefined) return;

      setFiles((files) => {
        const newFiles = files;
        //@ts-ignore
        newFiles.push({ ...e, file_type: type });
        return newFiles;
      });

      setFileNames((fileNames) => {
        const newFileNames = fileNames;
        //@ts-ignore
        newFileNames.push(fn);
        return newFileNames;
      });

      setOptionsFileUpload((optionsFileUpload) => {
        const newOptions = optionsFileUpload;
        //@ts-ignore
        newOptions.push({ label: `${fn.split('__')[1]} - ${type}`, value: fn });
        return newOptions;
      });

      const addFile = async () => {
        try {
          setButtonSpinningUpload(true);

          const fd = new FormData();
          fd.append('document', e);
          fd.append('file_type', type);

          const response = await axios.post(`${process.env.REACT_APP_API_URL ?? `/api`}/admin/products/${params.id}/variants/${params.variantId}/upload`, fd, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
            },
          });
          const data = response.data;
          if (data.status === 'success') {
            setFileUploadActive(true);
            setUpdate(!update);
          }
        } catch (error) {
          const axiosError = error as AxiosError;
          if (axiosError.response) {
            console.log(axiosError.response.data);
          }
          setSaveError(true);
        } finally {
          setButtonSpinningUpload(false);
        }
      };
      await addFile();
      toggleModalFileUpload();
    },
    [params.id, typeOptions, update, toggleModalFileUpload],
  );

  const handleConfirmFileUpload = useCallback(() => {
    if (isAdderFileUpload) {
      handleFileUploadAdd(tmpFileUploadValue, txtFileUploadValue, selectedType);
    }
    // else {
    //   handleFileUploadModify(tmpFileUploadValue, txtFileUploadValue, selectedFileUpload[0]);
    // }
  }, [isAdderFileUpload, handleFileUploadAdd, tmpFileUploadValue, txtFileUploadValue, selectedType, selectedFileUpload]);

  const handleAddFileUpload = useCallback(
    (_dropFiles: any, acceptedFiles: any, _rejectedFiles: any) => {
      toggleModalFileUpload();
      toggleIsAdderFileUpload(true);
      const blob = acceptedFiles[0];
      const file = new File([blob], params.id + '__' + acceptedFiles[0].name);
      setTxtFileUploadValue(file.name);
      // @ts-ignore
      setTmpFileUploadValue((tmpFileUploadValue) => file);
    },
    [toggleModalFileUpload, toggleIsAdderFileUpload, params.id],
  );

  const handleSelectionFileUpload = useCallback((e: any) => {
    setSelectedFileUpload(e);
  }, []);

  // UseEffect to update the pricing table when the rows or columns change
  useEffect(() => {
    if (hasPricingTable) {
      const rowLabelsTmp = pricingTable.row_labels.map((rowLabel, rowIndex) => {
        const tmpRows = [];
        tmpRows.push(rowLabel);
        pricingTable.column_labels.forEach((columnLabel, columnIndex) => {
          const currentInput = [rowIndex, columnIndex];
          const stringMatrix = JSON.stringify(currentInput);
          tmpRows.push(<EditableRows state={pricingTable.values} setState={handleUpdatePricingTableValues} type="number" selfPointer={stringMatrix} />);
        });
        return tmpRows;
      });

      const columnContents: Array<TColumnContent> = pricingTable.column_labels.map((columnLabel) => ({
        columnType: 'numeric',
        columnTitle: columnLabel,
      }));

      setColumnLabels([{ columnType: 'text', columnTitle: '' }, ...columnContents]);
      setRowsLabels(rowLabelsTmp);
    }
  }, [rows, columns, pricingTable, pricingTableExists]);

  // UseEffect to update the DISCOUNT pricing table when the rows or columns change
  useEffect(() => {
    if (hasDiscountPricingTable) {
      const rowLabelsTmp = discountPricingTable.row_labels.map((rowLabel, rowIndex) => {
        const tmpRows = [];
        tmpRows.push(rowLabel);
        discountPricingTable.column_labels.forEach((columnLabel, columnIndex) => {
          const currentInput = [rowIndex, columnIndex];
          const stringMatrix = JSON.stringify(currentInput);
          tmpRows.push(
            <EditableRows state={discountPricingTable.values} setState={handleUpdateDiscountPricingTableValues} type="number" selfPointer={stringMatrix} />,
          );
        });
        return tmpRows;
      });

      const columnContents: Array<TColumnContent> = discountPricingTable.column_labels.map((columnLabel) => ({
        columnType: 'numeric',
        columnTitle: columnLabel,
      }));

      setColumnDiscountLabels([{ columnType: 'text', columnTitle: '' }, ...columnContents]);
      setRowsDiscountLabels(rowLabelsTmp);
    }
  }, [rowsDiscount, columnsDiscount, discountPricingTable, hasDiscountPricingTable]);

  /**
   * Enabler Field markup
   */

  const enablerFieldMarkup = (
    <>
      <LegacyCard title="Campo abilitante">
        <LegacyCard.Section>
          <Checkbox
            label="Questo prodotto prevede un campo abilitante per procedere con lo sconto."
            checked={hasEnablerFields}
            onChange={handleHasEnablerFieldChange}
          />
        </LegacyCard.Section>
        {hasEnablerFields && (
          <LegacyCard.Section>
            {enablerField && (
              <LegacyCard.Section>
                <LegacyStack alignment="center">
                  <LegacyStack.Item fill>
                    <TextField
                      label={`Campo quotazione`}
                      value={String(enablerField.name)}
                      onChange={(name) => handleEnablerFieldChange(name)}
                      autoComplete="off"
                    />
                    <br></br>
                    <FormLayout>
                      <TextField
                        label="Valore"
                        type="text"
                        value={String(enablerField.value)}
                        autoComplete="off"
                        onChange={(value) => handleEnableFieldValueChange(value)}
                        connectedLeft={
                          <Select
                            label="Regola"
                            labelHidden
                            value={enablerField.rule}
                            options={blockingConditionsRuleOptions}
                            onChange={(value: RuleTypes) => handleEnableFieldRuleChange(value)}
                          />
                        }
                      />
                    </FormLayout>
                  </LegacyStack.Item>
                </LegacyStack>
              </LegacyCard.Section>
            )}
          </LegacyCard.Section>
        )}
      </LegacyCard>
    </>
  );

  /**
   * Pricing rules markup
   */
  const pricingRulesMarkup = (
    <>
      <DndContext sensors={sensorsPricingRules} collisionDetection={closestCenter} onDragEnd={handleDragEndPricingRules}>
        <SortableContext items={pricingRulesOptions} strategy={verticalListSortingStrategy}>
          {pricingRulesOptions.map((id, index) => (
            <SortableItemPricingRule
              key={`${pricingRules[index]?.name ?? 'pricingRule'}-${id}`}
              id={id}
              pricingRuleItem={pricingRules[index]}
              nameOptions={pricingRulesNames}
              typeOptions={pricingRulesTypes}
              onDelete={handleRemovePricingRule}
              onSave={handleAddPricingRule}
              onUpdate={handleUpdatePricingRule}
            />
          ))}
        </SortableContext>
      </DndContext>
      <LegacyCard.Section>
        <Button plain removeUnderline icon={PlusMinor as any} onClick={handleAddPricingRulesOption}>
          Aggiungi regola
        </Button>
      </LegacyCard.Section>
    </>
  );

  /**
   * Delete file
   */
  const handleDelFileUpload = useCallback(
    async (fileKey: string) => {
      if (!fileKey) return;

      const indexToDelete = optionsFileUpload.findIndex((op) => op.value === fileKey);
      if (indexToDelete === -1) return;

      try {
        await axios.delete(`${process.env.REACT_APP_API_URL ?? `/api`}/admin/products/${params.id}/variants/${params.variantId}/files/${fileKey}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
          },
        });

        setFileDeletedActive(true);
      } catch (error) {
        console.log(error);
        setError(true);
      }

      setFiles((prevFiles) => {
        const newFiles = [...prevFiles];
        newFiles.splice(indexToDelete, 1);
        return newFiles;
      });

      setFileNames((prevNames) => {
        const newNames = [...prevNames];
        newNames.splice(indexToDelete, 1);
        return newNames;
      });

      setFileID((prevIDs) => {
        const newIDs = [...prevIDs];
        newIDs.splice(indexToDelete, 1);
        return newIDs;
      });

      setOptionsFileUpload((prevOptions) => {
        const newOptions = [...prevOptions];
        newOptions.splice(indexToDelete, 1);
        return newOptions;
      });

      handleSelectionFileUpload(['']);
    },
    [optionsFileUpload, fileNames, files, fileID, handleSelectionFileUpload, params.id],
  );

  /**
   * Download single file
   */
  const handleOpenFile = useCallback(
    async (fileKey: string) => {
      try {
        const data = await fetch(`${process.env.REACT_APP_API_URL ?? '/api'}/admin/products/${params.id}/variants/${params.variantId}/files/${fileKey}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
          },
        });
        const response = await data.blob();
        saveAs(response, fileKey);
      } catch (error) {
        console.log(error);
        setError(true);
      }
    },
    [params.id],
  );

  const fileUpload = !tmpFileUploadValue && <DropZone.FileUpload actionHint="Accetta pdf" />;
  const uploadedFile = tmpFileUploadValue && (
    <LegacyStack>
      <div>
        {/*
        // @ts-ignore */}
        {tmpFileUploadValue.name.split('__')[1]}{' '}
        <Text variant="bodySm" as="p">
          {tmpFileUploadValue.size} bytes
        </Text>
      </div>
    </LegacyStack>
  );

  const fileUploadJSX = (
    <div>
      <LegacyStack vertical>
        {/* DropZone per il caricamento dei file */}
        <DropZone allowMultiple={false} onDrop={handleAddFileUpload} disabled={missingUserError}>
          <DropZone.FileUpload />
        </DropZone>

        <Layout>
          <Layout.Section>
            <ResourceList
              resourceName={{ singular: 'documento', plural: 'documenti' }}
              items={optionsFileUpload}
              renderItem={(file) => {
                return (
                  <ResourceItem id={file.value} onClick={() => handleOpenFile(file.value)}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon source={NoteMinor as any} />
                        <span style={{ marginLeft: '8px' }}>{file.label}</span>
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <Tooltip content="Scarica" preferredPosition="above">
                          <div onClick={(e) => e.stopPropagation()}>
                            <Button plain icon={<Icon source={AddNoteMajor as any} />} onClick={() => handleOpenFile(file.value)} />
                          </div>
                        </Tooltip>

                        <Tooltip content="Elimina" preferredPosition="above">
                          <div onClick={(e) => e.stopPropagation()}>
                            <Button plain icon={<Icon source={DeleteMajor as any} />} onClick={() => handleDelFileUpload(file.value)} />
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  </ResourceItem>
                );
              }}
            />
          </Layout.Section>
        </Layout>
      </LegacyStack>

      <div>
        <Modal
          open={activeFileUpload}
          onClose={toggleModalFileUpload}
          title={isAdderFileUpload ? 'Conferma Aggiunta' : 'Conferma Modifica'}
          secondaryActions={[
            {
              content: 'Chiudi',
              onAction: toggleModalFileUpload,
            },
          ]}
        >
          <Modal.Section>
            <FormLayout>
              <FormLayout.Group>
                <DropZone onDrop={handleTmpFileUploadValue}>
                  {uploadedFile}
                  {fileUpload}
                </DropZone>
              </FormLayout.Group>
              <FormLayout.Group>
                <Select label="Tipologia di documento" options={typeOptions} onChange={(e: VariantFileType) => setSelectedType(e)} value={selectedType} />
              </FormLayout.Group>
              <FormLayout.Group>
                <Button primary onClick={handleConfirmFileUpload} loading={buttonSpinningUpload}>
                  {isAdderFileUpload ? 'Conferma Aggiunta' : 'Conferma Modifica'}
                </Button>
              </FormLayout.Group>
            </FormLayout>
          </Modal.Section>
        </Modal>
      </div>
    </div>
  );

  /**
   * Renewal handler
   */
  const handleRenewalAutomaticChange = useCallback(
    (value: any) => {
      // If renewal.automatic is true, set it to false and also set renewal.to_be_confirmed to false
      if (renewal.automatic) {
        setRenewal({ automatic: false, to_be_confirmed: false });
        setIsDirty(true);
        return;
      }
      setRenewal({ ...renewal, automatic: value });
      setIsDirty(true);
    },
    [renewal],
  );

  const handleRenewalToBeConfirmedChange = useCallback(
    (value: any) => {
      setRenewal({ ...renewal, to_be_confirmed: value });
      setIsDirty(true);
    },
    [renewal],
  );

  /**
   * Company autocomplete
   */
  const updateCompanyText = useCallback(
    (value: any) => {
      setCompanyInputValue(value);

      if (!companyLoading) {
        setCompanyLoading(true);
      }

      setTimeout(() => {
        if (value === '') {
          setCompanyOptions(deselectedCompaniesOptions);
          setCompanyLoading(false);
          return;
        }
        const filterRegex = new RegExp(value, 'i');
        const resultOptions = deselectedCompaniesOptions.filter((option: any) => option.label.match(filterRegex));
        setCompanyOptions(resultOptions);
        setCompanyLoading(false);
      }, 300);
    },
    [deselectedCompaniesOptions, companyLoading],
  );

  const updateCompanySelection = useCallback(
    (selected: any) => {
      // Check if emptyFields is true
      if (emptyFields.company) {
        setEmptyFields((emptyFields) => ({ ...emptyFields, company: false }));
      }

      const selectedCompany = selected.map((selectedItem: any) => {
        const matchedOption = companyOptions.find((option) => {
          return option.value.match(selectedItem);
        });
        return matchedOption;
      });
      setSelectedCompanyOptions(selected);
      setCompanyInputValue(selectedCompany[0].label);

      // Add company
      const company = selectedCompany[0].company;
      setCompany(company);
      setIsDirty(true);
    },
    [companyOptions],
  );

  const companyTextField = (
    <Autocomplete.TextField
      onChange={updateCompanyText}
      label="Compagnia"
      value={companyInputValue}
      prefix={<Icon source={SearchMinor as any} color="base" />}
      placeholder="Cerca compagnia"
      autoComplete="off"
      error={emptyFields.company && 'Per favore seleziona una compagnia'}
    />
  );

  const emptyCompanyState = (
    <React.Fragment>
      <div style={{ textAlign: 'center' }}>
        <TextContainer>Nessun cliente trovato</TextContainer>
      </div>
    </React.Fragment>
  );

  /**
   * Company markup
   *
   * Display autocomplete if company is not selected
   * When company is loaded, display company details
   */
  const companyMarkup =
    company === null ? (
      <LegacyStack vertical>
        <Autocomplete
          options={companyOptions}
          selected={selectedCompanyOptions}
          onSelect={updateCompanySelection}
          loading={companyLoading}
          textField={companyTextField}
          emptyState={emptyCompanyState}
        />
      </LegacyStack>
    ) : (
      <HorizontalStack blockAlign="center" gap="5">
        {company.logo && <Thumbnail size="medium" alt={company.logo.title} source={process.env.REACT_APP_BLOB_IMAGES_URL + company.logo.key} />}
        <Text as="span" fontWeight="semibold">
          {company.name}
        </Text>
        <div style={{ marginLeft: 'auto' }}>
          <Button
            icon={CancelSmallMinor as any}
            onClick={() => {
              setCompany(null);
              setSelectedCompanyOptions([]);
              setCompanyInputValue('');
            }}
            plain
          />
        </div>
      </HorizontalStack>
    );

  /**
   * Contextual Save Bar
   */
  const handleDiscard = useCallback(() => {
    setIsDirty(false);

    // Reset all the fields to the defaultState
    setName(defaultState.name);
    setDescription(defaultState.description);
    setPrice(defaultState.price);
    setSalePrice(defaultState.salePrice);
    setHasWarranties(defaultState.hasWarranties);
    setVariantWarranties(defaultState.variantWarranties);
  }, []);

  const contextualSaveBarMarkup = isDirty ? (
    <ContextualSaveBar
      message={'Modifiche non salvate'}
      saveAction={{
        onAction: handleSave,
        loading: buttonSpinning,
      }}
      discardAction={{
        onAction: handleDiscard,
        discardConfirmationModal: true,
      }}
      contextControl={contextControlMarkup}
    />
  ) : null;

  /**
   * Variant list markup
   */
  const variantListMarkup = (
    <ul className="variantList">
      {variants.map((variant, index) => (
        <li key={index} className="variantItem">
          <UnstyledLink
            url={variant._id !== params.variantId ? `/admin/products/${params.id}/variants/${variant._id}` : null}
            className={`variantItemStack ${variant._id === params.variantId && 'active'}`}
          >
            <LegacyStack>
              <LegacyStack.Item>
                <Icon source={FraudProtectMajor as any} />
              </LegacyStack.Item>
              <LegacyStack.Item>
                <span className="variantItemLink">{variant.name}</span>
              </LegacyStack.Item>
            </LegacyStack>
          </UnstyledLink>
        </li>
      ))}
    </ul>
  );

  /** Notifaction email removal */
  const removeTag = useCallback(
    (tag: string) => () => {
      setSelectedTags((previousTags) => previousTags.filter((previousTag) => previousTag !== tag));
      setIsDirty(true);
    },
    [],
  );

  /** Notification Tag Markup */
  const notificationTagMarkup = selectedTags.map((option) => (
    <Tag key={option} onRemove={removeTag(option)}>
      {option}
    </Tag>
  ));

  /** Add shared notification mail when button clicked based on newTag state */
  const addTag = () => {
    const tagError = !validateEmail(newTag);
    if (tagError) {
      setTagError(true);
      return;
    } else {
      setTagError(false);
      newTag !== '' && !selectedTags.includes(newTag) && setSelectedTags([...selectedTags, newTag]);
      setNewTag('');
    }
  };

  const handleNewTagOnChange = useCallback((value: any) => {
    setNewTag(value);
    setIsDirty(true);
  }, []);

  /**
   * Error markups & toast
   */
  const toastMarkup = active ? <Toast content="I dati sono stati aggiornati con successo." onDismiss={toggleActive} /> : null;
  const toastFileDeletedMarkup = fileDeletedActive ? <Toast content="Il file è stato eliminato con successo" onDismiss={toggleFileDeletedActive} /> : null;

  const saveErrorMarkup = saveError && (
    <Layout.Section fullWidth>
      <Banner title="Si è verificato un errore nel salvataggio dei dati" status="critical" onDismiss={() => setSaveError(false)}>
        <p>Si è pregati di riprovare più tardi.</p>
      </Banner>
    </Layout.Section>
  );

  const errorMarkup = error && (
    <Layout.Section fullWidth>
      <Banner title="Si è verificato un errore" status="critical" onDismiss={() => setError(false)}>
        <p>Si è pregati di riprovare più tardi.</p>
      </Banner>
    </Layout.Section>
  );

  /**
   * Page markup
   */
  const actualPageMarkup = (
    <Page title="Modifica variante" backAction={{ content: 'Prodotto', url: `/admin/products/${params.id}` }}>
      <Layout>
        {/* Banner */}
        {saveErrorMarkup}
        {errorMarkup}

        <Layout.Section secondary>
          {/* Status */}
          <LegacyCard title="Varianti">
            <div className="variantListContainer">
              <Scrollable shadow>{variantListMarkup}</Scrollable>
            </div>
          </LegacyCard>
        </Layout.Section>

        <Layout.Section>
          {/* Name, description, to be quoted & global policy */}
          <LegacyCard>
            <LegacyCard.Section>
              <FormLayout>
                <TextField label="Nome" value={name} onChange={handleNameChange} autoComplete="off" error={emptyFields.name && 'Il nome è obbligatorio'} />
                <TextField label="Descrizione" value={description} onChange={handleDescriptionChange} autoComplete="off" />
                <Checkbox label="Preventivo da quotare manualmente" checked={toBeQuoted} onChange={handleToBeQuotedChange} />
                <Checkbox label="Prevede l'emissione diretta" checked={directEmission} onChange={handleDirectEmissionChange} />
              </FormLayout>
            </LegacyCard.Section>
            {/* Bank Transfer */}
            <LegacyCard.Section>
              <FormLayout>
                <Checkbox label="Da pagare tramite bonifico bancario" checked={bankTransfer} onChange={handleBankTransferChange} />
              </FormLayout>
            </LegacyCard.Section>
            {/* Individual sell  */}
            <LegacyCard.Section>
              <FormLayout>
                <Checkbox label="Può solo essere venduto individualmente" checked={individualSale} onChange={handleIndividualSaleChange} />
                {individualSale && (
                  <FormLayout.Group>
                    <TextField label="Template email preventivo" value={quoteEmailTemplate} onChange={handleQuoteEmailTemplateChange} autoComplete="off" />
                  </FormLayout.Group>
                )}
                {individualSale && (
                  <FormLayout.Group>
                    <TextField label="Template email polizza" value={policyEmailTemplate} onChange={handlePolicyEmailTemplateChange} autoComplete="off" />
                  </FormLayout.Group>
                )}
                {individualSale && (
                  <FormLayout.Group>
                    <TextField label="Template email rinnovo" value={renewalEmailTemplate} onChange={handleRenewalEmailTemplateChange} autoComplete="off" />
                  </FormLayout.Group>
                )}
              </FormLayout>
            </LegacyCard.Section>
            {/* Group policy */}
            <LegacyCard.Section>
              <FormLayout>
                <Checkbox label="È una polizza collettiva" checked={groupPolicy} onChange={handleGroupPolicyChange} />
                {groupPolicy && (
                  <FormLayout.Group>
                    <TextField label="Numero polizza" value={groupPolicyNumber} onChange={handleGroupPolicyNumberChange} autoComplete="off" />
                    <TextField
                      label="Prefisso numero inclusione"
                      value={groupPolicyInclusionNumber}
                      onChange={handleGroupPolicyInclusionNumberChange}
                      autoComplete="off"
                    />
                  </FormLayout.Group>
                )}
                {groupPolicy && (
                  <FormLayout.Group>
                    <TextField label="Broker" value={groupPolicyBroker} onChange={handleGroupPolicyBrokerChange} autoComplete="off" />
                    <TextField label="Ramo" value={groupPolicyBranch} onChange={handleGroupPolicyBranchChange} autoComplete="off" />
                  </FormLayout.Group>
                )}
              </FormLayout>
            </LegacyCard.Section>
            {/* Disable sending notification */}
            <LegacyCard.Section>
              <FormLayout>
                <Checkbox label="Disattiva l'invio delle notifiche al cliente" checked={disableNotifications} onChange={handleDisableNotificationsChange} />
              </FormLayout>
            </LegacyCard.Section>
            <LegacyCard.Section>
              {/* Company */}
              {companyMarkup}
            </LegacyCard.Section>
          </LegacyCard>

          {/* Renewal */}
          <LegacyCard title="Rinnovo">
            <LegacyCard.Section>
              <FormLayout>
                <Checkbox label="Il prodotto prevedere il rinnovo automatico" checked={renewal.automatic} onChange={handleRenewalAutomaticChange} />
                {renewal.automatic && (
                  <FormLayout.Group>
                    <Checkbox
                      label="Il rinnovo prevedere di essere confermato?"
                      checked={renewal.to_be_confirmed}
                      onChange={handleRenewalToBeConfirmedChange}
                    />
                  </FormLayout.Group>
                )}
              </FormLayout>
            </LegacyCard.Section>
          </LegacyCard>

          {/* Shared Notifications */}
          <LegacyCard title="Notifiche condivise">
            <LegacyCard.Section>
              <Checkbox
                label="Per questo prodotto sono previste notifiche condivise"
                checked={hasSharedNotification}
                onChange={handleHasSharedNotificationChange}
              />
            </LegacyCard.Section>
            {hasSharedNotification ? (
              <LegacyCard.Section>
                <FormLayout>
                  <LegacyStack spacing="tight">{notificationTagMarkup}</LegacyStack>
                  <TextField
                    label="Nuova mail"
                    value={newTag}
                    onChange={handleNewTagOnChange}
                    autoComplete="off"
                    error={tagError && 'Il formato della mail non è valido'}
                  />
                  <Button onClick={() => addTag()}>Aggiungi Mail</Button>
                </FormLayout>
              </LegacyCard.Section>
            ) : null}
          </LegacyCard>

          {/* Automation Bot */}
          <LegacyCard title="Automazione Ordine">
            <LegacyCard.Section>
              <Checkbox
                label="Per questo prodotto sono previste automazioni della registrazione dell'ordine"
                checked={hasAutomationBot}
                onChange={handleHasAutomationBotChange}
              />
            </LegacyCard.Section>
            {hasAutomationBot ? (
              <LegacyCard.Section>
                <FormLayout>
                  <LegacyStack spacing="tight">{notificationTagMarkup}</LegacyStack>
                  <Select label="Nome automazione" value={automationName} options={automationOptions} onChange={handleAutomationSelectChange} />
                </FormLayout>
                {automationBotError}
              </LegacyCard.Section>
            ) : null}
          </LegacyCard>

          {/* Variant type */}
          <LegacyCard sectioned title="Tipologia di variante">
            <FormLayout>
              <FormLayout.Group>
                <Select label="Tipologia di variante" options={variantTypeOptions} onChange={handleVariantTypeChange} value={selectedVariantType} />
                {selectedVariantType === 'api' && (
                  <TextField
                    label="Idenficatore api"
                    value={apiIdentifier}
                    onChange={handleApiIdentifierChange}
                    autoComplete="off"
                    error={emptyFields.api_identifier && "Il nome dell'identificatore api è obbligatorio"}
                  />
                )}
              </FormLayout.Group>
            </FormLayout>
          </LegacyCard>

          {/* Pricing */}
          <LegacyCard title="Prezzi">
            <LegacyCard.Section>
              <Checkbox
                label="Questo prodotto prevede l'utilizzo dei campi di quotazione per il calcolo del prezzo"
                checked={quotation}
                onChange={handleQuotationChange}
              />
            </LegacyCard.Section>
            {!quotation && (
              <LegacyCard.Section>
                <FormLayout>
                  <TextField
                    label="Prezzo"
                    type="currency"
                    placeholder="0,00"
                    value={price}
                    onChange={handlePriceChange}
                    autoComplete="off"
                    suffix="€"
                    error={emptyFields.price && 'Il prezzo è obbligatorio'}
                  />
                  <TextField
                    label="Prezzo promozionale"
                    type="currency"
                    placeholder="0,00"
                    value={salePrice}
                    onChange={handleSalePriceChange}
                    autoComplete="off"
                    suffix={
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <p style={{ marginRight: '5px' }}>€</p>
                        <Tooltip content="Inserisci qui il prezzo promozionale che vuoi mostrare al cliente">
                          <Icon source={QuestionMarkMajor as any} />
                        </Tooltip>
                      </div>
                    }
                  />
                </FormLayout>
              </LegacyCard.Section>
            )}
          </LegacyCard>

          {/** Campi di quotazione */}
          <LegacyCard title="Campi di quotazione">
            <LegacyCard.Section>{quotationMarkup}</LegacyCard.Section>
          </LegacyCard>

          {/* Pricing Table */}
          {hasPricingTable && (
            <LegacyCard
              title="Tabella dei prezzi"
              actions={[
                {
                  content: 'Elimina tabella',
                  onAction: handleRemovePricingTable,
                },
              ]}
            >
              <LegacyCard.Section>
                <FormLayout>
                  <Checkbox label="Il prezzo va calcolato come range" checked={pricingTable.is_range} onChange={handlePricingRangeChange} />
                  <TextField label="Campo righe" type="text" value={rows} onChange={handleRowsChange} autoComplete="off" />
                  <TextField label="Campo colonne" type="text" value={columns} onChange={handleColumnsChange} autoComplete="off" />
                  <div className="pricingTableButtons">
                    <Button onClick={handleAddPricingTable} disabled={pricingTableExists}>
                      Fatto
                    </Button>
                    <Button onClick={handleUpdatePricingTable}>Aggiorna</Button>
                  </div>
                </FormLayout>
              </LegacyCard.Section>
              {columnLabels && rowLabels && <EditableTable columnContents={columnLabels} rows={rowLabels} />}
            </LegacyCard>
          )}
          {/* Sconto */}
          <LegacyCard title="Sconto">
            <LegacyCard.Section>
              <Checkbox label="Questo prodotto prevede uno sconto" checked={hasDiscount} onChange={handleHasDiscountChange} />
            </LegacyCard.Section>

            {hasDiscount && discount && (
              <LegacyCard.Section>
                <FormLayout>
                  <Select label="Tipologia di sconto" value={discount.type} options={discountValueTypeOptions} onChange={handleDiscountTypeChange} />

                  {(discount.type === 'fixed_amount' || discount.type === 'percentage') && (
                    <TextField
                      label="Valore"
                      type="text"
                      placeholder="0,00"
                      value={discount.value?.toString()}
                      onChange={handleDiscountValueChange}
                      autoComplete="off"
                      suffix={discount.type === 'percentage' ? '%' : undefined}
                    />
                  )}

                  {discount.type === 'pricing_table' && (
                    <LegacyCard
                      title="Tabella dei prezzi"
                      actions={[
                        {
                          content: 'Elimina tabella',
                          onAction: handleRemoveDiscountPricingTable,
                        },
                      ]}
                    >
                      <LegacyCard.Section>
                        <FormLayout>
                          <Checkbox label="Il prezzo va calcolato come range" checked={discountPricingTable.is_range} onChange={handlePricingRangeChange} />
                          <TextField label="Campo righe" type="text" value={rowsDiscount} onChange={handleDiscountRowsChange} autoComplete="off" />
                          <TextField label="Campo colonne" type="text" value={columnsDiscount} onChange={handleDiscountColumnsChange} autoComplete="off" />
                          <div className="pricingTableButtons">
                            <Button onClick={handleAddDiscountPricingTable}>Fatto</Button>
                            <Button onClick={handleUpdateDiscountPricingTable}>Aggiorna</Button>
                          </div>
                        </FormLayout>
                      </LegacyCard.Section>
                      {columnDiscountLabels && rowDiscountLabels && <EditableTable columnContents={columnDiscountLabels} rows={rowDiscountLabels} />}
                    </LegacyCard>
                  )}
                  {/**Enabler field */}
                  {enablerFieldMarkup}
                </FormLayout>
              </LegacyCard.Section>
            )}
          </LegacyCard>

          {/* Blocking conditions */}
          <LegacyCard title="Condizioni bloccanti">
            <LegacyCard.Section>
              <Checkbox
                label="Questo prodotto prevede delle condizioni bloccanti"
                checked={hasBlockingConditions}
                onChange={handleHasBlockingConditionsChange}
              />
            </LegacyCard.Section>
            {hasBlockingConditions && blockingConditions.length > 0 && (
              <LegacyCard.Section>
                {blockingConditions.map((condition, index) => (
                  <LegacyCard.Section key={index}>
                    <LegacyStack alignment="center">
                      <LegacyStack.Item fill>
                        <TextField
                          key={index}
                          label={`Campo quotazione`}
                          value={condition.field}
                          onChange={(value) => handleBlockingConditionFieldChange(index, value)}
                          autoComplete="off"
                        />
                        <br></br>
                        <FormLayout>
                          {condition.conditions.map((subCondition, subIndex) => (
                            <LegacyCard sectioned key={subIndex}>
                              <TextField
                                label={`Regola ${subIndex + 1}`}
                                type="text"
                                value={subCondition.value}
                                autoComplete="off"
                                onChange={(value) => handleBlockingConditionValueChange(index, subIndex, value)}
                                connectedLeft={
                                  <Select
                                    label={`Regola ${subIndex + 1}`}
                                    labelHidden
                                    value={subCondition.rule}
                                    options={blockingConditionsRuleOptions}
                                    onChange={(value: RuleTypes) => handleBlockingConditionRuleChange(index, subIndex, value)}
                                  />
                                }
                                connectedRight={<Button icon={DeleteMinor as any} plain onClick={() => handleDeleteBlockingConditionRule(index, subIndex)} />}
                              />
                            </LegacyCard>
                          ))}
                        </FormLayout>
                        <LegacyCard.Section>
                          <Button plain removeUnderline icon={PlusMinor as any} onClick={() => handleAddBlockingConditionRule(index)}>
                            Aggiungi nuova regola
                          </Button>
                        </LegacyCard.Section>
                      </LegacyStack.Item>
                      <LegacyStack.Item>
                        <Button icon={DeleteMinor as any} plain onClick={() => handleDeleteBlockingCondition(index)} />
                      </LegacyStack.Item>
                    </LegacyStack>
                  </LegacyCard.Section>
                ))}
                <LegacyCard.Section>
                  <Button plain removeUnderline icon={PlusMinor as any} onClick={handleAddBlockingCondition}>
                    Aggiungi nuova condizione
                  </Button>
                </LegacyCard.Section>
              </LegacyCard.Section>
            )}
          </LegacyCard>

          {/* Pricing Rules */}
          {quotation && (
            <LegacyCard title="Regole di prezzo">
              <LegacyCard.Section>
                <Checkbox
                  label="Questo prodotto prevede delle regole da applicare alla quotazione"
                  checked={hasPricingRules}
                  onChange={handleHasPricingRulesChange}
                />
              </LegacyCard.Section>
              {hasPricingRules ? pricingRulesMarkup : null}
            </LegacyCard>
          )}

          {/* Commission */}
          <LegacyCard title="Provvigioni">
            <LegacyCard.Section>
              <Checkbox label="Questo prodotto prevede delle provvigioni per il broker" checked={hasCommission} onChange={handleHasCommissionChange} />
            </LegacyCard.Section>
            {hasCommission ? (
              <LegacyCard.Section>
                <FormLayout>
                  <FormLayout.Group condensed>
                    <Select
                      label="Tipo di provvigione"
                      options={commissionValueTypeOptions}
                      onChange={handleCommissionTypeChange}
                      value={commission.value_type}
                    />
                    <TextField
                      label="Valore"
                      type="currency"
                      placeholder="0,00"
                      value={commission.value}
                      onChange={handleCommissionValueChange}
                      autoComplete="off"
                      suffix={commission.value_type === 'fixed_amount' ? '€' : '%'}
                      min={0}
                      max={commission.value_type !== 'fixed_amount' ? 100 : undefined}
                      error={emptyFields.commission_value && 'Il valore è obbligatorio'}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </LegacyCard.Section>
            ) : null}
          </LegacyCard>

          {/* Management price */}
          <LegacyCard title="Fattura di gestione">
            <LegacyCard.Section>
              <Checkbox
                label="Questo prodotto prevede l'aggiunta di una fattura di gestione"
                checked={hasManagementPrice}
                onChange={handleHasManagementPriceChange}
              />
            </LegacyCard.Section>
            {hasManagementPrice ? (
              <LegacyCard.Section>
                <FormLayout>
                  <FormLayout.Group condensed>
                    <Select
                      label="Tipo di pricing"
                      options={commissionValueTypeOptions}
                      onChange={handleManagementPriceTypeChange}
                      value={managementPrice.value_type}
                    />
                    <TextField
                      label="Valore"
                      type="currency"
                      placeholder="0,00"
                      value={managementPrice.value}
                      onChange={handleManagementPriceValueChange}
                      autoComplete="off"
                      suffix={managementPrice.value_type === 'fixed_amount' ? '€' : '%'}
                      min={0}
                      max={managementPrice.value_type !== 'fixed_amount' ? 100 : undefined}
                      error={emptyFields.management_price_value && 'Il valore è obbligatorio'}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </LegacyCard.Section>
            ) : null}
          </LegacyCard>

          {/* Warranties */}
          <LegacyCard title="Garanzie">
            <LegacyCard.Section>
              <Checkbox label="Questo prodotto ha garanzie" checked={hasWarranties} onChange={handleHasWarrantiesChange} />
            </LegacyCard.Section>
            {hasWarranties && warrantyMarkup}
          </LegacyCard>

          {/* Files */}
          <LegacyCard title="Documenti">
            <LegacyCard.Section>{fileUploadJSX}</LegacyCard.Section>
          </LegacyCard>
        </Layout.Section>

        <Layout.Section />
      </Layout>
    </Page>
  );

  // ---- Loading ----
  const loadingPageMarkup = (
    <SkeletonPage>
      <Layout>
        <Layout.Section>
          <LegacyCard sectioned>
            <TextContainer>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={9} />
            </TextContainer>
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );

  // ---- Error ----
  const errorPageMarkup = (
    <Page>
      <Layout>
        <Layout.Section>
          <LegacyCard sectioned>
            <EmptyState
              heading="Nessun prodotto presente a questo indirizzo"
              image="https://cdn.shopify.com/shopifycloud/web/assets/v1/08f1b23a19257042c52beca099d900b0.svg"
            >
              <p>Controlla l'URL e riprova oppure usa la barra di ricerca per trovare ciò di cui hai bisogno.</p>
            </EmptyState>
          </LegacyCard>
        </Layout.Section>
      </Layout>
    </Page>
  );

  /**
   * Upload markup
   */
  const toastFileUploadMarkup = fileUploadActive ? <Toast content="Il file è stato caricato con successo" onDismiss={toggleFileUploadActive} /> : null;

  const pageMarkup = isLoading ? loadingPageMarkup : loadError ? errorPageMarkup : actualPageMarkup;

  return (
    <Frame
      topBar={<TopBarMarkup user={user} handleMobileNavigation={handleMobileNavigation} />}
      navigation={<NavigationMarkup user={user} />}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigationActive}
      skipToContentTarget={skipToContentRef}
    >
      {contextualSaveBarMarkup}
      {pageMarkup}
      {toastMarkup}
      {toastFileUploadMarkup}
      {toastFileDeletedMarkup}
    </Frame>
  );
}
